/**
 * Cockpit CFO — Onglet Rapprochement Wave (Phase 2/3)
 * ---------------------------------------------------
 * - Import XLSX/CSV Wave
 * - KPIs période
 * - Table transactions (filtres statut/score/recherche)
 * - Actions : match manuel (avec allocations), unmatch, reconcile auto
 * - Frais Wave tracking (validation propriétaire)
 * - Rapport mensuel Excel + clôture/verrou + audit
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Wallet, Upload, RefreshCw, FileSpreadsheet, Lock, Unlock, Loader2,
  CheckCircle2, AlertTriangle, AlertCircle, Search, Filter, Coins,
  ShieldAlert, Link2, X, ChevronDown, TrendingUp, Download,
  History, Users, Send, ArrowDownRight, ArrowUpRight,
  Sparkles, Building2, Bookmark, BookmarkPlus, Trash2, Ban,
  ChevronLeft, ChevronRight, ListChecks, ClipboardPaste, Tag, RotateCcw, ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BulkActionBar, confirmBulk } from '../common/BulkActionBar';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import WaveAiProposalsDialog from './WaveAiProposalsDialog';
import WaveCategorizeDialog from './WaveCategorizeDialog';
import WaveInflowsPanel from './WaveInflowsPanel';
import { useWavePendingCount } from '../../hooks/useWavePendingCount';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ================== HELPERS ================== */
const fxof = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${Math.round(v).toLocaleString('fr-FR')} XOF`;
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v); }
};

// Propriétaire de frais "effectif" : tant que le frais n'est pas validé par un
// humain (feeValidated), il est considéré comme non classé — peu importe la
// valeur par défaut posée à l'import.
const effectiveFeeOwner = (tx) => (tx.feeValidated ? (tx.feeOwner || 'unknown') : 'unknown');

const STATUS_META = {
  non_rapprochee:         { label: 'Non rapprochée',        color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  rapprochee_auto:        { label: 'Rapprochée (auto)',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rapprochee_manuelle:    { label: 'Rapprochée (manuelle)', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rapprochement_partiel:  { label: 'Partiel',                color: 'bg-blue-100 text-blue-700 border-blue-200' },
  paiement_groupe:        { label: 'Groupé',                 color: 'bg-violet-100 text-violet-700 border-violet-200' },
  ecart_detecte:          { label: 'Écart',                  color: 'bg-rose-100 text-rose-700 border-rose-200' },
  annulee_reversee:       { label: 'Annulée/reversée',       color: 'bg-rose-100 text-rose-700 border-rose-200' },
  a_verifier:             { label: 'À vérifier',             color: 'bg-amber-100 text-amber-700 border-amber-200' },
  non_concernee_wave:     { label: 'Hors flux',              color: 'bg-teal-50 text-teal-700 border-teal-200' },
  payee_hors_wave:        { label: 'Payée hors Wave',        color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const formatPeriodLabel = (p) => {
  if (!p || p === 'all') return 'Toutes périodes';
  const [y, m] = p.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${MONTHS_FR[idx]} ${y}`;
};
const shiftPeriod = (p, delta) => {
  if (!p || p === 'all') return p;
  const [y, m] = p.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/* ================== PERIOD SELECT ================== */
const PeriodSelect = ({ value, onChange, testId = 'wave-period-select' }) => {
  const isAll = !value || value === 'all';
  const [y, m] = isAll ? [null, null] : value.split('-').map(Number);
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 3 + i);
  const setMonth = (mm) => onChange(`${y || currentYear}-${String(mm).padStart(2, '0')}`);
  const setYear = (yy) => onChange(`${yy}-${String(m || (now.getMonth() + 1)).padStart(2, '0')}`);
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
        <button
          onClick={() => !isAll && onChange(shiftPeriod(value, -1))}
          disabled={isAll}
          className="h-8 w-7 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 border-r border-zinc-200"
          data-testid={`${testId}-prev`}
          title="Mois précédent"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <select
          value={isAll ? '' : m}
          onChange={(e) => setMonth(parseInt(e.target.value, 10))}
          disabled={isAll}
          className="h-8 pl-2 pr-1 text-xs bg-transparent focus:outline-none disabled:opacity-40 min-w-[92px] font-medium"
          data-testid={`${testId}-month`}
        >
          {isAll && <option value="">—</option>}
          {MONTHS_FR.map((label, i) => (
            <option key={i} value={i + 1}>{label}</option>
          ))}
        </select>
        <select
          value={isAll ? '' : y}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          disabled={isAll}
          className="h-8 pl-1 pr-1.5 text-xs bg-transparent focus:outline-none disabled:opacity-40 font-medium border-l border-zinc-200"
          data-testid={`${testId}-year`}
        >
          {isAll && <option value="">—</option>}
          {years.map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
        <button
          onClick={() => !isAll && onChange(shiftPeriod(value, 1))}
          disabled={isAll}
          className="h-8 w-7 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 border-l border-zinc-200"
          data-testid={`${testId}-next`}
          title="Mois suivant"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        onClick={() => onChange(isAll ? currentPeriod() : 'all')}
        className={`h-8 px-2.5 text-[11px] font-semibold rounded-lg border transition-colors ${
          isAll
            ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
            : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
        }`}
        data-testid={`${testId}-all-toggle`}
        title={isAll ? 'Revenir au mois courant' : 'Voir toutes les périodes'}
      >
        {isAll ? '✓ Toutes' : 'Toutes'}
      </button>
    </div>
  );
};

/* ================== KPI CARD V2 ================== */
const KpiCardV2 = ({ label, value, sub, icon: Icon, tone = 'zinc', testId, valueTone }) => {
  const tones = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-100' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-100' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-100' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-100' },
    zinc:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    border: 'border-zinc-200' },
  };
  const t = tones[tone] || tones.zinc;
  const valueColor = valueTone === 'negative' ? 'text-rose-600' : 'text-zinc-900';
  return (
    <div
      data-testid={testId}
      className="bg-white border border-zinc-200 rounded-xl p-3.5 hover:shadow-sm transition-shadow"
    >
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <span className="text-[10.5px] font-semibold text-zinc-500 leading-tight">{label}</span>
        {Icon && (
          <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${t.bg} ${t.border} border shrink-0`}>
            <Icon className={`h-3.5 w-3.5 ${t.text}`} />
          </div>
        )}
      </div>
      <div className={`text-[19px] font-bold tabular-nums leading-none ${valueColor}`} title={String(value)}>{value}</div>
      {sub && <div className="text-[10.5px] text-zinc-500 mt-1.5 leading-tight truncate" title={sub}>{sub}</div>}
    </div>
  );
};

/* ================== DONUT RATE (mini pie for reconciliation rate) ================== */
const DonutRate = ({ percent, matched, total, valueRate, testId = 'wave-donut-rate' }) => {
  const p = Math.max(0, Math.min(100, percent || 0));
  const r = 22;
  const c = 2 * Math.PI * r;
  const offset = c - (p / 100) * c;
  const color = p >= 90 ? '#10b981' : p >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-semibold text-zinc-500">Taux de rapprochement</span>
      </div>
      <div className="flex items-center gap-3">
        <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
          <circle cx="28" cy="28" r={r} fill="none" stroke="#e4e4e7" strokeWidth="5" />
          <circle
            cx="28" cy="28" r={r} fill="none"
            stroke={color} strokeWidth="5"
            strokeDasharray={c} strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 28 28)"
            style={{ transition: 'stroke-dashoffset 500ms ease' }}
          />
          <text x="28" y="32" textAnchor="middle" className="fill-zinc-900" style={{ fontSize: '11px', fontWeight: 700 }}>
            {p.toFixed(1)}%
          </text>
        </svg>
        <div className="min-w-0">
          <div className="text-[16px] font-bold tabular-nums text-zinc-900">{matched} / {total}</div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">Tx rapprochées</div>
          {valueRate != null && (
            <div className="text-[10px] text-zinc-500 mt-0.5 tabular-nums" data-testid="wave-donut-value-rate"
                 title="Part des montants sortis effectivement rapprochés (en XOF)">
              En valeur : <span className={`font-bold ${valueRate >= 90 ? 'text-emerald-700' : valueRate >= 70 ? 'text-amber-700' : 'text-rose-700'}`}>{valueRate}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================== PIPELINE DE RAPPROCHEMENT (barre empilée) ================== */
const PipelineBar = ({ kpis, onSegmentClick }) => {
  const total = kpis?.transactionsCount || 0;
  if (!total) return null;
  const matched = (kpis.matched || 0) + (kpis.grouped || 0) + (kpis.partial || 0);
  const segments = [
    { key: 'matched',   label: 'Rapprochées',       value: matched,             color: 'bg-emerald-500', filter: null },
    { key: 'toVerify',  label: 'À vérifier',        value: kpis.toVerify || 0,  color: 'bg-amber-400',   filter: 'a_verifier' },
    { key: 'ecart',     label: 'Écarts',            value: kpis.ecart || 0,     color: 'bg-rose-500',    filter: 'ecart_detecte' },
    { key: 'excluded',  label: 'Hors flux',         value: kpis.excluded || 0,  color: 'bg-teal-400',    filter: 'non_concernee_wave' },
    { key: 'cancelled', label: 'Annulées',          value: kpis.cancelled || 0, color: 'bg-zinc-400',    filter: 'annulee_reversee' },
    { key: 'unmatched', label: 'Non rapprochées',   value: kpis.unmatched || 0, color: 'bg-zinc-200',    filter: 'non_rapprochee' },
  ].filter(s => s.value > 0);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid="wave-pipeline-bar">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-semibold text-zinc-500 uppercase tracking-wider">Pipeline de rapprochement</span>
        <span className="text-[10.5px] text-zinc-400 tabular-nums">{total} tx</span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-zinc-100">
        {segments.map((s) => (
          <button
            key={s.key}
            onClick={() => onSegmentClick?.(s.filter)}
            className={`${s.color} hover:opacity-80 transition-opacity`}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={`${s.label} : ${s.value} (${((s.value / total) * 100).toFixed(1)}%)`}
            data-testid={`wave-pipeline-seg-${s.key}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
        {segments.map((s) => (
          <button key={s.key} onClick={() => onSegmentClick?.(s.filter)}
                  className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-900">
            <span className={`h-2 w-2 rounded-full ${s.color}`} />
            {s.label} <span className="font-semibold tabular-nums">{s.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ================== KPI CARD ================== */
const WaveKpi = ({ label, value, sub, icon: Icon, tone = 'zinc', testId }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    zinc: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  };
  return (
    <div data-testid={testId} className="bg-white border border-zinc-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</span>
        {Icon && (
          <div className={`h-6 w-6 rounded-md flex items-center justify-center border ${tones[tone]}`}>
            <Icon className="h-3 w-3" />
          </div>
        )}
      </div>
      <div className="text-xl font-bold tabular-nums text-zinc-900" title={String(value)}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1 truncate" title={sub}>{sub}</div>}
    </div>
  );
};

/* ================== MANUAL MATCH DIALOG ================== */
const ManualMatchDialog = ({ open, onClose, tx, onSuccess }) => {
  const [opQuery, setOpQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [allocations, setAllocations] = useState([]); // {operationId, operationNumber, allocatedAmount, kind}
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  const [multiRefs, setMultiRefs] = useState('');
  const [multiSearching, setMultiSearching] = useState(false);
  const [multiNotFound, setMultiNotFound] = useState([]);

  useEffect(() => {
    if (!open) {
      setAllocations([]); setNote(''); setOpQuery(''); setCandidates([]);
      setMultiMode(false); setMultiRefs(''); setMultiNotFound([]);
    }
  }, [open]);

  const searchOps = useCallback(async () => {
    if (!opQuery || opQuery.length < 3) { setCandidates([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/transport-requests`, { params: { q: opQuery, limit: 15 } });
      const items = Array.isArray(r.data) ? r.data : (r.data.items || r.data.data || []);
      setCandidates(items.slice(0, 15).map(o => ({
        id: o.id,
        operationNumber: o.operationNumber || o.number,
        carrierPrice: o.carrierPrice || 0,
        driverName: o.assignedDriver?.name || o.driverName,
      })));
    } catch (e) {
      toast({ title: 'Erreur recherche opérations', variant: 'destructive' });
    } finally { setSearching(false); }
  }, [opQuery]);

  const addAllocation = (op) => {
    if (allocations.find(a => a.operationId === op.id)) return;
    const remaining = (tx?.amountSent || 0) - allocations.reduce((s, a) => s + Number(a.allocatedAmount || 0), 0);
    setAllocations([...allocations, {
      operationId: op.id,
      operationNumber: op.operationNumber,
      allocatedAmount: Math.max(0, Math.min(op.carrierPrice || 0, remaining)),
      waveFeeAllocated: 0,
      kind: 'total',
    }]);
  };

  const updateAlloc = (i, field, value) => {
    const copy = [...allocations];
    copy[i] = { ...copy[i], [field]: value };
    setAllocations(copy);
  };

  const removeAlloc = (i) => setAllocations(allocations.filter((_, idx) => idx !== i));

  // Recherche MULTIPLE : coller plusieurs N° d'opérations (séparés par espace/virgule/retour ligne)
  const searchMulti = async () => {
    const queries = multiRefs.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.length >= 3);
    if (queries.length === 0) {
      toast({ title: 'Saisissez au moins un N° d\'opération', variant: 'destructive' });
      return;
    }
    setMultiSearching(true);
    setMultiNotFound([]);
    try {
      const r = await axios.post(`${API}/wave/operations/lookup`, { queries });
      const { found = [], notFound = [] } = r.data;
      setMultiNotFound(notFound);
      let added = 0;
      const existing = new Set(allocations.map(a => a.operationId));
      const newAllocs = [];
      let remaining = (tx?.amountSent || 0) - allocations.reduce((s, a) => s + Number(a.allocatedAmount || 0), 0);
      for (const op of found) {
        if (existing.has(op.id)) continue;
        const amount = Math.max(0, Math.min(op.carrierPrice || 0, remaining));
        remaining -= amount;
        newAllocs.push({
          operationId: op.id,
          operationNumber: op.operationNumber,
          allocatedAmount: amount,
          waveFeeAllocated: 0,
          kind: 'total',
        });
        added += 1;
      }
      setAllocations([...allocations, ...newAllocs]);
      toast({
        title: `${added} opération(s) ajoutée(s)`,
        description: notFound.length > 0 ? `${notFound.length} introuvable(s) : ${notFound.join(', ').slice(0, 100)}` : undefined,
        variant: notFound.length > 0 && added === 0 ? 'destructive' : undefined,
      });
    } catch (e) {
      toast({ title: 'Erreur recherche multiple', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setMultiSearching(false); }
  };

  const totalAllocated = allocations.reduce((s, a) => s + Number(a.allocatedAmount || 0), 0);
  const remaining = (tx?.amountSent || 0) - totalAllocated;

  const submit = async () => {
    if (allocations.length === 0) {
      toast({ title: 'Aucune allocation', description: 'Ajoutez au moins une opération.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/wave/transactions/${tx.id}/match`, {
        allocations: allocations.map(a => ({
          operationId: a.operationId,
          allocatedAmount: Number(a.allocatedAmount || 0),
          waveFeeAllocated: Number(a.waveFeeAllocated || 0),
          kind: a.kind,
        })),
        note: note || null,
      });
      toast({ title: 'Rapprochement enregistré' });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur rapprochement', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (!tx) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="wave-match-dialog">
        <DialogHeader>
          <DialogTitle>Rapprochement manuel — {tx.waveRef}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-sm p-3 bg-zinc-50 rounded-lg border border-zinc-200">
            <div><div className="text-[10px] text-zinc-500 uppercase">Bénéficiaire</div><div className="font-medium">{tx.beneficiaryName || '—'}</div></div>
            <div><div className="text-[10px] text-zinc-500 uppercase">Envoyé</div><div className="font-medium tabular-nums">{fxof(tx.amountSent)}</div></div>
            <div><div className="text-[10px] text-zinc-500 uppercase">Frais</div><div className="font-medium tabular-nums">{fxof(tx.waveFee)}</div></div>
            <div><div className="text-[10px] text-zinc-500 uppercase">Total sorti</div><div className="font-medium tabular-nums text-rose-700">{fxof(tx.amountDebited)}</div></div>
          </div>

          {tx.comment && (
            <div className="text-xs text-zinc-600 italic p-2 bg-amber-50 border border-amber-200 rounded">
              Commentaire Wave : « {tx.comment} »
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold block">
                {multiMode ? 'Recherche multiple (collez plusieurs N° d\'opérations)' : 'Rechercher une opération (N° BAO- ou nom chauffeur)'}
              </Label>
              <button type="button" onClick={() => setMultiMode(!multiMode)}
                      className="text-[11px] text-violet-600 hover:text-violet-800 font-semibold flex items-center gap-1"
                      data-testid="wave-multi-toggle">
                <ClipboardPaste className="h-3 w-3" />
                {multiMode ? 'Recherche simple' : 'Recherche multiple'}
              </button>
            </div>
            {multiMode ? (
              <div className="space-y-2">
                <Textarea
                  data-testid="wave-multi-refs"
                  placeholder={'Collez plusieurs N° séparés par espace, virgule ou retour à la ligne :\nBAO-OP-2026-0001 BAO-OP-2026-0002, BAO-OP-2026-0003'}
                  rows={3}
                  value={multiRefs}
                  onChange={(e) => setMultiRefs(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={searchMulti} disabled={multiSearching} data-testid="wave-multi-search-btn"
                          className="bg-violet-600 hover:bg-violet-700">
                    {multiSearching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ListChecks className="h-3.5 w-3.5 mr-1.5" />}
                    Rechercher &amp; ajouter tout
                  </Button>
                  {multiNotFound.length > 0 && (
                    <span className="text-[10px] text-rose-600">Introuvables : {multiNotFound.join(', ').slice(0, 80)}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  data-testid="wave-search-op"
                  placeholder="ex : BAO-OP-2026… ou nom du chauffeur"
                  value={opQuery}
                  onChange={(e) => setOpQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchOps()}
                />
                <Button onClick={searchOps} disabled={searching} data-testid="wave-search-btn">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>

          {candidates.length > 0 && (
            <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-40 overflow-y-auto">
              {candidates.map(op => (
                <button key={op.id} type="button"
                  onClick={() => addAllocation(op)}
                  data-testid={`wave-candidate-${op.operationNumber}`}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-3 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{op.operationNumber || op.id}</div>
                    <div className="text-xs text-zinc-500 truncate">{op.driverName || '—'} · Transporteur {fxof(op.carrierPrice)}</div>
                  </div>
                  <span className="text-emerald-600 text-xs font-semibold">+ Ajouter</span>
                </button>
              ))}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Allocations ({allocations.length})</Label>
              <div className="text-xs">
                <span className="text-zinc-500">Alloué :</span> <span className="font-semibold tabular-nums">{fxof(totalAllocated)}</span>
                {' · '}
                <span className={`font-semibold tabular-nums ${remaining === 0 ? 'text-emerald-700' : remaining < 0 ? 'text-rose-700' : 'text-amber-700'}`}>
                  Reste {fxof(remaining)}
                </span>
              </div>
            </div>
            {allocations.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-4 border border-dashed border-zinc-200 rounded-lg">
                Aucune opération allouée. Recherchez et ajoutez-en une ci-dessus.
              </div>
            ) : (
              <div className="space-y-2">
                {allocations.map((a, i) => (
                  <div key={a.operationId} className="flex items-center gap-2 p-2 border border-zinc-200 rounded-lg" data-testid={`wave-alloc-${i}`}>
                    <span className="font-mono text-xs flex-1 truncate">{a.operationNumber}</span>
                    <select value={a.kind} onChange={(e) => updateAlloc(i, 'kind', e.target.value)} className="text-xs border border-zinc-200 rounded px-1 py-1">
                      <option value="total">Total</option>
                      <option value="avance">Avance</option>
                      <option value="solde">Solde</option>
                      <option value="partiel">Partiel</option>
                    </select>
                    <Input type="number" value={a.allocatedAmount} onChange={(e) => updateAlloc(i, 'allocatedAmount', e.target.value)}
                           className="w-32 text-xs" data-testid={`wave-alloc-amount-${i}`} />
                    <Input type="number" placeholder="Frais" value={a.waveFeeAllocated} onChange={(e) => updateAlloc(i, 'waveFeeAllocated', e.target.value)}
                           className="w-24 text-xs" />
                    <Button variant="ghost" size="sm" onClick={() => removeAlloc(i)}><X className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold mb-1 block">Note (optionnelle)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Contexte / justificatif…" data-testid="wave-match-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || allocations.length === 0} data-testid="wave-match-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            Rapprocher
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ================== REOPEN DIALOG ================== */
const ReopenDialog = ({ open, onClose, period, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason || reason.trim().length < 5) {
      toast({ title: 'Motif obligatoire (min 5 caractères)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/wave/months/${period}/reopen`, { reason });
      toast({ title: `Mois ${period} rouvert` });
      onSuccess?.();
      onClose();
      setReason('');
    } catch (e) {
      toast({ title: 'Erreur réouverture', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wave-reopen-dialog">
        <DialogHeader><DialogTitle>Rouvrir le mois {period}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            La réouverture d&apos;un mois clôturé sera tracée dans le journal d&apos;audit. Justifiez précisément.
          </div>
          <Label className="text-xs font-semibold">Motif de réouverture</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="ex : Correction d'un écart identifié après clôture — tx pt-XXX…"
                    data-testid="wave-reopen-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="wave-reopen-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Unlock className="h-4 w-4 mr-2" />}
            Rouvrir le mois
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ================== MAIN COMPONENT ================== */
const WaveReconciliationTab = () => {
  const [period, setPeriod] = useState(currentPeriod());
  const [kpis, setKpis] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTx, setTotalTx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categoriesAvailable, setCategoriesAvailable] = useState([]);
  const [catDialog, setCatDialog] = useState({ open: false, txIds: [], txLabel: null });
  const [outflowOnly, setOutflowOnly] = useState(true);
  const [query, setQuery] = useState('');
  const [monthStatus, setMonthStatus] = useState(null);
  const [reports, setReports] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [matchDialog, setMatchDialog] = useState({ open: false, tx: null });
  const [reopenDialog, setReopenDialog] = useState(false);
  // Phase D — Pre-close check (informational, non-blocking)
  const [preCloseCheck, setPreCloseCheck] = useState(null);
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkReconciling, setBulkReconciling] = useState(false);
  // Recherches sauvegardées
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  // IA
  const [aiDialog, setAiDialog] = useState({ open: false, result: null, analyzing: false });
  // Diagnostic rapprochement
  const [diagDialog, setDiagDialog] = useState({ open: false, tx: null, data: null, loading: false });
  // Onglets Vue d'ensemble / Transactions / Exceptions / Frais / Rapports
  const [activeTab, setActiveTab] = useState('overview');
  // Compteur "à traiter" partagé pour badge sur onglet Encaissements
  const wavePending = useWavePendingCount({ enabled: true });
  // Filtre spécifique à l'onglet Frais Wave (par propriétaire)
  const [feesOwnerFilter, setFeesOwnerFilter] = useState('');
  const [feesSelection, setFeesSelection] = useState([]);
  const [feesBulkBusy, setFeesBulkBusy] = useState(false);

  // Modale clôture sécurisée
  const [closeDialog, setCloseDialog] = useState(false);
  const fileInputRef = useRef(null);
  const isAllPeriods = !period || period === 'all';

  const openDiagnose = useCallback(async (tx) => {
    setDiagDialog({ open: true, tx, data: null, loading: true });
    try {
      const r = await axios.get(`${API}/wave/transactions/${tx.id}/diagnose`);
      setDiagDialog({ open: true, tx, data: r.data, loading: false });
    } catch (e) {
      toast({
        title: 'Erreur diagnostic',
        description: e?.response?.data?.detail || 'Erreur',
        variant: 'destructive',
      });
      setDiagDialog({ open: false, tx: null, data: null, loading: false });
    }
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const periodParam = (period && period !== 'all') ? period : undefined;
      const [kRes, tRes, mRes, rRes, pcRes] = await Promise.all([
        axios.get(`${API}/wave/cockpit`, { params: { period: periodParam } }),
        axios.get(`${API}/wave/transactions`, {
          params: {
            period: periodParam,
            match_status: statusFilter || undefined,
            is_outflow: outflowOnly ? true : undefined,
            category: categoryFilter || undefined,
            q: query || undefined,
            limit: pageSize,
            skip: page * pageSize,
          },
        }),
        periodParam
          ? axios.get(`${API}/wave/months/${periodParam}/status`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
        axios.get(`${API}/wave/reports`, { params: { period: periodParam } }).catch(() => ({ data: { items: [] } })),
        periodParam
          ? axios.get(`${API}/wave/months/${periodParam}/pre-close-check`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setKpis(kRes.data);
      setTransactions(tRes.data.items || []);
      setTotalTx(tRes.data.total || 0);
      setMonthStatus(mRes.data);
      setReports(rRes.data.items || []);
      setPreCloseCheck(pcRes.data);
    } catch (e) {
      toast({ title: 'Erreur cockpit Wave', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setLoading(false); setRefreshing(false); }
  }, [period, statusFilter, outflowOnly, query, page, pageSize, categoryFilter]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  // Période intelligente (22/06/2026) : si le mois courant est vide, ouvrir sur le
  // dernier mois réellement alimenté (évite un module vide à l'arrivée).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/wave/periods/latest`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.latestPeriod || data.currentPeriodCount > 0) return;
        if (data.latestPeriod === data.currentPeriod) return;
        setPeriod(data.latestPeriod);
        toast({
          title: `Période ouverte sur ${formatPeriodLabel(data.latestPeriod)}`,
          description: 'Aucune transaction Wave sur le mois courant — dernier mois alimenté affiché.',
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Reset page + sélection quand les filtres changent
  useEffect(() => { setPage(0); setSelectedIds(new Set()); }, [period, statusFilter, outflowOnly, query, pageSize, categoryFilter]);
  // Catégories disponibles (filtre + dialog)
  useEffect(() => {
    axios.get(`${API}/wave/categories`)
      .then(r => setCategoriesAvailable(r.data.items || []))
      .catch(() => {});
  }, [catDialog.open]);
  // Onglet Exceptions : réduit à statuts anormaux si aucun filtre spécifique n'est déjà appliqué à un statut d'exception
  useEffect(() => {
    if (activeTab === 'exceptions' && !['non_rapprochee', 'a_verifier', 'ecart_detecte'].includes(statusFilter)) {
      setStatusFilter('non_rapprochee');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Helper : switch d'onglet initié par l'utilisateur depuis la barre d'onglets.
  // Nettoie les filtres pour éviter de garder un filtre hérité d'un autre onglet
  // (ex : rester en "Statut=Non rapprochée" quand on va sur "Frais Wave" donne un tableau vide).
  // NB : les boutons d'insights qui font `setStatusFilter(...) + setActiveTab(...)` ne passent pas
  // par ce helper — leur intention filtrée est donc préservée.
  const switchTab = (key) => {
    if (key === 'overview' || key === 'transactions' || key === 'fees' || key === 'reports') {
      setStatusFilter('');
      setCategoryFilter('');
    } else if (key === 'exceptions') {
      setStatusFilter('non_rapprochee');
      setCategoryFilter('');
    }
    setActiveTab(key);
  };

  const loadSavedSearches = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wave/saved-searches`);
      setSavedSearches(r.data.items || []);
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => { loadSavedSearches(); }, [loadSavedSearches]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/wave/statements/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.duplicate) {
        toast({ title: 'Fichier déjà importé', description: r.data.message });
      } else {
        toast({
          title: `Import réussi — ${r.data.rowsImported} lignes`,
          description: `Période détectée : ${r.data.periodMonth || '—'}. Doublons ignorés : ${r.data.rowsDuplicated}. Analyse IA en cours…`,
        });
        if (r.data.periodMonth && r.data.periodMonth !== period) setPeriod(r.data.periodMonth);
        // 🤖 Analyse IA automatique post-import
        runAiAnalysis({ period: r.data.periodMonth || period });
      }
      load();
    } catch (e) {
      toast({ title: 'Erreur import', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const runAutoReconcile = async () => {
    setReconciling(true);
    try {
      const r = await axios.post(`${API}/wave/reconcile/auto`, null, { params: { period, only_unmatched: true } });
      toast({
        title: `Rapprochement automatique — ${r.data.matchedAuto} match(s)`,
        description: `${r.data.scanned} scannées · ${r.data.toVerify} à vérifier · ${r.data.unmatched} non rapprochées`,
      });
      load();
    } catch (e) {
      toast({ title: 'Erreur rapprochement auto', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setReconciling(false); }
  };

  // 🤖 Analyse IA — propositions de rapprochement (période ou sélection)
  const runAiAnalysis = async ({ period: p, transactionIds } = {}) => {
    setAiDialog({ open: true, result: null, analyzing: true });
    try {
      const r = await axios.post(`${API}/wave/reconcile/ai`, {
        period: transactionIds ? undefined : (p || period),
        transactionIds: transactionIds || undefined,
      });
      setAiDialog({ open: true, result: r.data, analyzing: false });
    } catch (e) {
      setAiDialog({
        open: true, analyzing: false,
        result: { error: e?.response?.data?.detail || 'Erreur analyse IA', proposals: [] },
      });
    }
  };

  // Rapprochement bulk sur la sélection
  const runBulkReconcile = async () => {
    if (selectedIds.size === 0) return;
    setBulkReconciling(true);
    try {
      const r = await axios.post(`${API}/wave/reconcile/bulk`, { transactionIds: [...selectedIds] });
      toast({
        title: `Rapprochement bulk — ${r.data.matchedAuto} match(s) / ${selectedIds.size} sélectionnée(s)`,
        description: `${r.data.toVerify} à vérifier · ${r.data.unmatched} non rapprochées`,
      });
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast({ title: 'Erreur rapprochement bulk', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setBulkReconciling(false); }
  };

  // Bulk ignore / delete / export CSV (Fév 2026)
  const runBulkIgnore = async () => {
    if (selectedIds.size === 0) return;
    setBulkReconciling(true);
    try {
      const { data } = await axios.post(`${API}/wave/transactions/bulk-ignore`, {
        transactionIds: [...selectedIds], reason: 'hors_perimetre',
      });
      toast({ title: 'Ignorées en masse', description: `${data?.updated ?? selectedIds.size} tx` });
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast({ title: 'Erreur ignore bulk', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setBulkReconciling(false); }
  };
  const runBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} transaction(s) Wave ?`)) return;
    setBulkReconciling(true);
    try {
      const { data } = await axios.post(`${API}/wave/transactions/bulk-delete`, {
        transactionIds: [...selectedIds],
      });
      toast({ title: 'Suppression en masse', description: `${data?.deleted ?? selectedIds.size} tx supprimée(s)` });
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast({ title: 'Erreur delete bulk', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setBulkReconciling(false); }
  };
  const runBulkExport = () => {
    if (selectedIds.size === 0) return;
    const rows = transactions.filter(t => selectedIds.has(t.id));
    const headers = ['Date', 'Réf Wave', 'Libellé', 'Contrepartie', 'Montant', 'Statut'];
    const csv = [
      headers.join(';'),
      ...rows.map(tx => [
        tx.transactionDate ? new Date(tx.transactionDate).toLocaleDateString('fr-FR') : '',
        tx.waveRef || '',
        (tx.description || '').replace(/;/g, ','),
        (tx.recipientName || tx.senderName || '').replace(/;/g, ','),
        tx.amount ?? '',
        tx.matchStatus || '',
      ].join(';')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wave_${period}_selection_${rows.length}tx.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: 'Export CSV', description: `${rows.length} ligne(s)` });
  };

  // Sélection multiple
  const toggleSelect = (txId) => {
    const next = new Set(selectedIds);
    if (next.has(txId)) next.delete(txId); else next.add(txId);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === transactions.length && transactions.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(transactions.map(t => t.id)));
  };

  // Recherches sauvegardées
  const saveCurrentSearch = async () => {
    const name = saveSearchName.trim();
    if (!name) { toast({ title: 'Nom requis', variant: 'destructive' }); return; }
    try {
      await axios.post(`${API}/wave/saved-searches`, {
        name,
        filters: { status: statusFilter, q: query, outflowOnly, period },
      });
      toast({ title: `Recherche « ${name} » sauvegardée` });
      setSaveSearchOpen(false);
      setSaveSearchName('');
      loadSavedSearches();
    } catch (e) {
      toast({ title: 'Erreur sauvegarde', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };
  const applySavedSearch = (s) => {
    const f = s.filters || {};
    setStatusFilter(f.status || '');
    setQuery(f.q || '');
    setOutflowOnly(f.outflowOnly !== false);
    if (f.period) setPeriod(f.period);
    toast({ title: `Recherche « ${s.name} » appliquée` });
  };
  const deleteSavedSearch = async (s, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/wave/saved-searches/${s.id}`);
      loadSavedSearches();
    } catch { toast({ title: 'Erreur suppression', variant: 'destructive' }); }
  };

  const handleUnmatch = async (tx) => {
    if (!window.confirm(`Retirer le rapprochement de ${tx.waveRef} ?`)) return;
    try {
      await axios.post(`${API}/wave/transactions/${tx.id}/unmatch`);
      toast({ title: 'Rapprochement retiré' });
      load();
    } catch (e) {
      toast({ title: 'Erreur unmatch', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const generateReport = async (format = 'xlsx') => {
    setGeneratingReport(true);
    try {
      const r = await axios.post(`${API}/wave/reports/monthly`, null, { params: { period, format } });
      toast({
        title: `Rapport ${format.toUpperCase()} généré`,
        description: `${r.data.fileName} — ${(r.data.fileSize / 1024).toFixed(1)} KB`,
      });
      load();
    } catch (e) {
      toast({ title: 'Erreur génération rapport', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setGeneratingReport(false); }
  };

  const downloadReport = async (reportId, fileName) => {
    try {
      const r = await axios.get(`${API}/wave/reports/${reportId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `wave_report_${period}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Erreur téléchargement', variant: 'destructive' });
    }
  };

  const closeMonth = async () => {
    try {
      await axios.post(`${API}/wave/months/${period}/close`);
      toast({ title: `Mois ${formatPeriodLabel(period)} clôturé` });
      setCloseDialog(false);
      load();
    } catch (e) {
      toast({ title: 'Erreur clôture', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const resetFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setQuery('');
    setOutflowOnly(true);
    setPage(0);
    setSelectedIds(new Set());
  };

  // Rétablir une tx catégorisée dans le flux de rapprochement (action réversible, pas de confirm)
  const handleUncategorize = async (tx) => {
    try {
      await axios.post(`${API}/wave/transactions/uncategorize`, { transactionIds: [tx.id] });
      toast({ title: 'Transaction rétablie dans le flux' });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const matchedTotal = (kpis?.matched || 0) + (kpis?.grouped || 0) + (kpis?.partial || 0);
  // Taux calculé hors tx catégorisées/annulées (elles ne sont pas à rapprocher)
  const reconcilableCount = Math.max(0, (kpis?.transactionsCount || 0) - (kpis?.excluded || 0) - (kpis?.cancelled || 0));
  const reconciliationRate = reconcilableCount > 0
    ? (matchedTotal / reconcilableCount) * 100
    : 0;
  const isLocked = monthStatus?.locked === true;
  const preCloseIssues = preCloseCheck?.issues || [];
  const preCloseBlockingIssues = preCloseIssues.filter(i => i.severity === 'blocking');

  const loadAudit = async () => {
    try {
      const r = await axios.get(`${API}/wave/audit`, { params: { period, limit: 100 } });
      setAuditLogs(r.data.items || []);
      setShowAudit(true);
    } catch (e) {
      toast({ title: 'Erreur audit', variant: 'destructive' });
    }
  };

  const validateFees = async (txIds, feeOwner) => {
    try {
      await axios.post(`${API}/wave/fees/validate`, { transactionIds: txIds, feeOwner });
      toast({ title: `Frais validés (${txIds.length}) → ${feeOwner}` });
      load();
    } catch (e) {
      toast({ title: 'Erreur validation frais', variant: 'destructive' });
    }
  };

  // Sélection multiple (25/09/2026) — attribution rapide du propriétaire des frais
  const bulkAssignFeeOwner = async (feeOwner, label) => {
    if (!feesSelection.length) return;
    if (!confirmBulk(feesSelection.length, `Attribution « ${label} »`)) return;
    setFeesBulkBusy(true);
    try {
      await axios.post(`${API}/wave/fees/validate`, {
        transactionIds: feesSelection, feeOwner,
      });
      toast({ title: `${feesSelection.length} frais attribué(s) → ${label}` });
      setFeesSelection([]);
      load();
    } catch (e) {
      toast({ title: 'Attribution en lot impossible',
        description: e?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setFeesBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wave-reconciliation-tab">
      {/* HEADER CFO */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
            <Wallet className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-[19px] font-bold text-zinc-900 leading-tight flex items-center gap-2">
              Rapprochement Wave
              {isLocked && (
                <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] font-semibold" data-testid="wave-locked-badge">
                  <Lock className="h-3 w-3 mr-1" /> Verrouillé
                </Badge>
              )}
            </div>
            <div className="text-[12.5px] text-zinc-500 mt-0.5">
              Importez, rapprochez et clôturez vos relevés Wave en toute confiance.
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1 font-medium text-zinc-600">
                <ClipboardPaste className="h-3 w-3" />
                Période : <span className="text-zinc-900">{formatPeriodLabel(period)}</span>
              </span>
              <span>·</span>
              <span>Dernière mise à jour : {fmtDate(new Date())}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <PeriodSelect value={period} onChange={setPeriod} testId="wave-period-select" />
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="wave-refresh" className="h-8">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
      </div>

      {/* BANNIÈRE STATUT DÉCISIONNELLE */}
      {!isAllPeriods && !isLocked && preCloseCheck && (
        preCloseIssues.length === 0 ? (
          <div className="p-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 flex items-center justify-between gap-3" data-testid="wave-status-banner-ready">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <div className="text-sm font-bold text-emerald-900">Prêt pour clôture</div>
                <div className="text-[12px] text-emerald-800">Aucune anomalie bloquante détectée. Vous pouvez clôturer ce mois en toute sécurité.</div>
              </div>
            </div>
            <Button size="sm" onClick={() => setCloseDialog(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wave-status-banner-close">
              <Lock className="h-3.5 w-3.5 mr-1.5" /> Voir le rapport de clôture
            </Button>
          </div>
        ) : (
          <div className={`p-3.5 rounded-xl border-2 flex items-start justify-between gap-3 ${
            preCloseBlockingIssues.length > 0 ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'
          }`} data-testid="wave-status-banner-blocked">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`h-9 w-9 rounded-full border flex items-center justify-center shrink-0 ${
                preCloseBlockingIssues.length > 0 ? 'bg-rose-100 border-rose-300' : 'bg-amber-100 border-amber-300'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${preCloseBlockingIssues.length > 0 ? 'text-rose-700' : 'text-amber-700'}`} />
              </div>
              <div className="min-w-0">
                <div className={`text-sm font-bold ${preCloseBlockingIssues.length > 0 ? 'text-rose-900' : 'text-amber-900'}`}>
                  {preCloseBlockingIssues.length > 0 ? 'Clôture bloquée' : 'Points d\'attention'}
                </div>
                <div className={`text-[12px] mt-0.5 ${preCloseBlockingIssues.length > 0 ? 'text-rose-800' : 'text-amber-800'}`}>
                  {preCloseIssues.map(i => i.label).join(' · ')}
                </div>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setActiveTab('exceptions')}
                    className={`shrink-0 ${preCloseBlockingIssues.length > 0 ? 'border-rose-300 text-rose-700 hover:bg-rose-100' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}`}
                    data-testid="wave-status-banner-see-issues">
              Voir les points bloquants
            </Button>
          </div>
        )
      )}
      {isAllPeriods && (
        <div className="p-2.5 rounded-xl border border-blue-200 bg-blue-50 flex items-center gap-2 text-[12px] text-blue-900" data-testid="wave-status-banner-all">
          <ClipboardPaste className="h-4 w-4 text-blue-600 shrink-0" />
          <span><b>Vue globale — toutes périodes.</b> La clôture n&apos;est disponible que sur un mois spécifique. Sélectionnez un mois pour clôturer.</span>
        </div>
      )}

      {/* ACTIONS BAR REGROUPÉES */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-white border border-zinc-200 rounded-xl shadow-sm">
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" data-testid="wave-import-input" />
        {/* Zone 1 — Actions principales */}
        <div className="flex items-center gap-1.5 pr-2 border-r border-zinc-200">
          <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing || isLocked}
                  className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="wave-import-btn">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            Importer relevé
          </Button>
          <Button size="sm" variant="outline" onClick={runAutoReconcile} disabled={reconciling || isLocked}
                  className="h-8" data-testid="wave-auto-reconcile">
            {reconciling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <TrendingUp className="h-3.5 w-3.5 mr-1.5" />}
            Rapprochement auto
          </Button>
          <Button size="sm" onClick={() => runAiAnalysis()} disabled={aiDialog.analyzing || isLocked}
                  className="bg-violet-600 hover:bg-violet-700 text-white h-8" data-testid="wave-ai-reconcile">
            {aiDialog.analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Assistant IA
          </Button>
        </div>
        {/* Zone 2 — Exports & journal (menu regroupé) */}
        <div className="flex items-center gap-1.5 px-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8" disabled={generatingReport} data-testid="wave-export-menu">
                {generatingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                Exporter
                <ChevronDown className="h-3 w-3 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem onClick={() => generateReport('xlsx')} disabled={isAllPeriods} data-testid="wave-generate-report">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Excel CFO
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => generateReport('pdf')} disabled={isAllPeriods} data-testid="wave-generate-pdf">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-rose-600" /> Rapport PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={loadAudit} data-testid="wave-audit-btn">
                <History className="h-3.5 w-3.5 mr-2 text-zinc-500" /> Journal d&apos;audit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1" />
        {/* Zone 3 — Action sensible clôture */}
        {isLocked ? (
          <Button size="sm" variant="outline" onClick={() => setReopenDialog(true)} data-testid="wave-reopen-btn"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 h-8">
            <Unlock className="h-3.5 w-3.5 mr-1.5" /> Rouvrir le mois
          </Button>
        ) : (
          <Button size="sm" onClick={() => setCloseDialog(true)} disabled={isAllPeriods}
                  data-testid="wave-close-btn"
                  className="bg-rose-50 border-2 border-rose-300 text-rose-700 hover:bg-rose-100 h-8 font-semibold">
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Clôturer le mois
          </Button>
        )}
      </div>

      {/* KPIs — 4 indicateurs majeurs + pipeline */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>
      ) : kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wave-kpis-cash">
            <DonutRate percent={reconciliationRate} matched={matchedTotal} total={reconcilableCount}
                       valueRate={kpis.valueRate} testId="wave-kpi-donut" />
            <KpiCardV2 label="Total sorti compte" value={fxof(kpis.totalDebited)}
                       sub={`Envoyé ${fxof(kpis.totalSent)} + frais ${fxof(kpis.totalFees)}`}
                       icon={ArrowUpRight} tone="rose" testId="wave-kpi-debited" />
            <KpiCardV2 label="Montant non rapproché" value={fxof(kpis.unmatchedAmount || 0)}
                       sub={`${kpis.unmatched} tx · ${kpis.toVerify || 0} à vérifier · ${kpis.ecart || 0} écart(s)`}
                       icon={AlertCircle} tone="amber" testId="wave-kpi-unmatched" />
            <KpiCardV2 label="Solde Wave fin" value={fxof(kpis.lastBalance)}
                       sub="Dernière tx de la période"
                       icon={Wallet} tone="blue"
                       valueTone={(kpis.lastBalance != null && kpis.lastBalance < 0) ? 'negative' : undefined}
                       testId="wave-kpi-balance" />
          </div>

          {/* Pipeline visuel du rapprochement */}
          <PipelineBar kpis={kpis} onSegmentClick={(filter) => {
            setStatusFilter(filter || '');
            setActiveTab(filter && ['non_rapprochee', 'a_verifier', 'ecart_detecte'].includes(filter) ? 'exceptions' : 'transactions');
          }} />
        </>
      )}

      {/* REPORTS ROW — remplacé par le contenu de l'onglet Rapports dédié plus bas */}


      {/* ONGLETS DU WORKSPACE */}
      <div className="flex items-center gap-1 border-b border-zinc-200" data-testid="wave-tabs">
        {[
          { key: 'overview',    label: 'Vue d\'ensemble',   icon: TrendingUp },
          { key: 'transactions', label: 'Transactions',     icon: Wallet },
          { key: 'inflows',      label: 'Encaissements',    icon: ArrowDownRight },
          { key: 'exceptions',   label: 'Exceptions',       icon: ShieldAlert },
          { key: 'fees',         label: 'Frais Wave',       icon: Coins },
          { key: 'reports',      label: 'Rapports',         icon: FileSpreadsheet },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              activeTab === key
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-800'
            }`}
            data-testid={`wave-tab-${key}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            {key === 'exceptions' && kpis && (kpis.unmatched + kpis.ecart) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold">
                {kpis.unmatched + kpis.ecart}
              </span>
            )}
            {key === 'inflows' && (wavePending?.total || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold" data-testid="wave-tab-inflows-badge"
                title={`${wavePending.merchantUnlinked} merchants + ${wavePending.agentUnclassified} agents à traiter`}>
                {wavePending.total > 99 ? '99+' : wavePending.total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* WORKSPACE : TABLE + PANNEAU INSIGHTS (uniquement transactions/exceptions) */}
      {['transactions', 'exceptions'].includes(activeTab) && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <div className="min-w-0 space-y-3">
      {/* FILTERS + RECHERCHES SAUVEGARDÉES */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-white border border-zinc-200 rounded-lg">
        <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Filter className="h-3 w-3" /> Filtres</div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs border border-zinc-200 rounded-md px-2 py-1" data-testid="wave-filter-status">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="text-xs border border-zinc-200 rounded-md px-2 py-1" data-testid="wave-filter-category">
          <option value="">Toutes catégories</option>
          {categoriesAvailable.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={outflowOnly} onChange={(e) => setOutflowOnly(e.target.checked)} data-testid="wave-filter-outflow" />
          Sorties uniquement
        </label>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Réf Wave · bénéficiaire · commentaire…"
                 className="pl-7 text-xs h-8" data-testid="wave-filter-query" />
        </div>
        <Button size="sm" variant="ghost" onClick={() => setSaveSearchOpen(true)}
                className="text-xs h-8 text-violet-700 hover:bg-violet-50" data-testid="wave-save-search-btn"
                title="Sauvegarder la recherche courante">
          <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Sauvegarder
        </Button>
        <Button size="sm" variant="ghost" onClick={resetFilters}
                className="text-xs h-8 text-zinc-600 hover:bg-zinc-100" data-testid="wave-filter-reset"
                title="Réinitialiser les filtres">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Réinitialiser
        </Button>
        <span className="text-xs text-zinc-500 font-semibold tabular-nums">{totalTx} tx</span>
      </div>

      {/* CHIPS DE FILTRES ACTIFS */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] px-1" data-testid="wave-active-filters">
        <span className="text-zinc-500 font-medium">Filtres actifs :</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
          Statut : {statusFilter ? STATUS_META[statusFilter]?.label : 'Tous'}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
          Période : {formatPeriodLabel(period)}
        </span>
        {outflowOnly && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            Sorties uniquement
          </span>
        )}
        {categoryFilter && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
            <Tag className="h-2.5 w-2.5" /> {categoryFilter}
            <button onClick={() => setCategoryFilter('')} className="hover:text-teal-900"><X className="h-2.5 w-2.5" /></button>
          </span>
        )}
        {query && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            « {query} »
          </span>
        )}
      </div>

      {/* Recherches sauvegardées (chips) */}
      {savedSearches.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid="wave-saved-searches">
          <Bookmark className="h-3 w-3 text-zinc-400" />
          {savedSearches.map((s) => (
            <button key={s.id} onClick={() => applySavedSearch(s)}
                    className="group inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-violet-50 border border-violet-200 text-violet-700 rounded-full hover:bg-violet-100 transition-colors"
                    data-testid={`wave-saved-search-${s.name}`}>
              {s.name}
              <Trash2 className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 text-rose-500 transition-opacity"
                      onClick={(e) => deleteSavedSearch(s, e)} />
            </button>
          ))}
        </div>
      )}

      {/* BARRE D'ACTIONS GROUPÉES (sélection multiple) */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-2.5 bg-violet-50 border border-violet-200 rounded-lg" data-testid="wave-bulk-bar">
          <span className="text-xs font-semibold text-violet-800">
            <ListChecks className="h-3.5 w-3.5 inline mr-1" />
            {selectedIds.size} sélectionnée(s)
          </span>
          <Button size="sm" variant="outline" onClick={runBulkReconcile} disabled={bulkReconciling || isLocked}
                  className="h-7 text-xs border-violet-300" data-testid="wave-bulk-auto">
            {bulkReconciling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
            Rapprochement auto ({selectedIds.size})
          </Button>
          <Button size="sm" onClick={() => runAiAnalysis({ transactionIds: [...selectedIds] })}
                  disabled={aiDialog.analyzing || isLocked}
                  className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid="wave-bulk-ai">
            <Sparkles className="h-3 w-3 mr-1" /> Analyse IA ({selectedIds.size})
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCatDialog({ open: true, txIds: [...selectedIds], txLabel: null })}
                  disabled={isLocked}
                  className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="wave-bulk-categorize">
            <Tag className="h-3 w-3 mr-1" /> Catégoriser ({selectedIds.size})
          </Button>
          <Button size="sm" variant="outline" onClick={runBulkIgnore} disabled={bulkReconciling || isLocked}
                  className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="wave-bulk-ignore">
            <Ban className="h-3 w-3 mr-1" /> Ignorer ({selectedIds.size})
          </Button>
          <Button size="sm" variant="outline" onClick={runBulkDelete} disabled={bulkReconciling || isLocked}
                  className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="wave-bulk-delete">
            <Trash2 className="h-3 w-3 mr-1" /> Supprimer ({selectedIds.size})
          </Button>
          <Button size="sm" variant="outline" onClick={runBulkExport}
                  className="h-7 text-xs" data-testid="wave-bulk-export">
            <Download className="h-3 w-3 mr-1" /> Export CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}
                  className="h-7 text-xs" data-testid="wave-bulk-clear">
            <X className="h-3 w-3 mr-1" /> Désélectionner
          </Button>
        </div>
      )}

      {/* TRANSACTIONS TABLE */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="wave-tx-table">
            <thead className="bg-zinc-50 text-zinc-600 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-2 text-center w-8">
                  <input type="checkbox"
                         checked={selectedIds.size === transactions.length && transactions.length > 0}
                         onChange={toggleSelectAll} data-testid="wave-select-all" />
                </th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Réf Wave</th>
                <th className="p-2 text-left">Bénéficiaire</th>
                <th className="p-2 text-left">Entreprise</th>
                <th className="p-2 text-right">Envoyé</th>
                <th className="p-2 text-right">Frais</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-left">Statut</th>
                <th className="p-2 text-right">Score</th>
                <th className="p-2 text-left">Ops liées</th>
                <th className="p-2 text-left">Frais propriétaire</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {transactions.length === 0 && !loading && (
                <tr><td colSpan={13} className="py-10 px-4">
                  {kpis && kpis.transactionsCount === 0 ? (
                    <div className="max-w-md mx-auto text-center" data-testid="wave-empty-state">
                      <div className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                        <Upload className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div className="text-sm font-semibold text-zinc-900 mb-1">
                        Aucun relevé Wave importé pour {formatPeriodLabel(period)}
                      </div>
                      <div className="text-xs text-zinc-500 mb-4">Pour démarrer :</div>
                      <ol className="text-[12px] text-zinc-600 space-y-1.5 text-left inline-block mb-4">
                        <li>1. Importez le relevé Wave CSV / XLSX</li>
                        <li>2. Lancez le rapprochement automatique</li>
                        <li>3. Vérifiez les exceptions</li>
                        <li>4. Générez le rapport CFO</li>
                      </ol>
                      <div>
                        <Button size="sm" onClick={() => fileInputRef.current?.click()}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="wave-empty-import-btn">
                          <Upload className="h-3.5 w-3.5 mr-1.5" /> Importer relevé Wave
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-zinc-400 text-xs">Aucune transaction ne correspond aux filtres. <button onClick={resetFilters} className="text-emerald-600 hover:underline ml-1">Réinitialiser</button></div>
                  )}
                </td></tr>
              )}
              {transactions.map((tx) => {
                const meta = STATUS_META[tx.matchStatus] || { label: tx.matchStatus, color: 'bg-zinc-100 text-zinc-700' };
                const canMatch = !isLocked && tx.matchStatus !== 'annulee_reversee';
                const canUnmatch = !isLocked && (tx.matchedOperationIds?.length || 0) > 0;
                const matchedOps = tx.matchedOps || [];
                const companies = [...new Set(matchedOps.map(o => o.companyName).filter(Boolean))];
                const carriers = [...new Set(matchedOps.map(o => o.carrierName).filter(Boolean))];
                return (
                  <tr key={tx.id} className={`hover:bg-zinc-50 ${selectedIds.has(tx.id) ? 'bg-violet-50/60' : ''}`} data-testid={`wave-tx-row-${tx.waveRef}`}>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={selectedIds.has(tx.id)} onChange={() => toggleSelect(tx.id)}
                             data-testid={`wave-select-${tx.waveRef}`} />
                    </td>
                    <td className="p-2 whitespace-nowrap text-zinc-600">{fmtDate(tx.date)}</td>
                    <td className="p-2 font-mono text-zinc-700 truncate max-w-[130px]" title={tx.waveRef}>{tx.waveRef}</td>
                    <td className="p-2 truncate max-w-[140px]" title={tx.beneficiaryName}>
                      <div className="font-medium">{tx.beneficiaryName || '—'}</div>
                      <div className="text-[10px] text-zinc-500">{tx.beneficiaryPhone || ''}</div>
                    </td>
                    <td className="p-2 truncate max-w-[150px]" data-testid={`wave-tx-company-${tx.waveRef}`}
                        title={[...companies, ...carriers].join(' · ')}>
                      {companies.length === 0 && carriers.length === 0 ? (
                        <span className="text-zinc-400">—</span>
                      ) : (
                        <>
                          {companies.length > 0 && (
                            <div className="font-medium text-zinc-800 truncate flex items-center gap-1">
                              <Building2 className="h-2.5 w-2.5 text-zinc-400 shrink-0" />
                              {companies[0]}{companies.length > 1 ? ` +${companies.length - 1}` : ''}
                            </div>
                          )}
                          {carriers.length > 0 && (
                            <div className="text-[10px] text-zinc-500 truncate">{carriers[0]}{carriers.length > 1 ? ` +${carriers.length - 1}` : ''}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums font-medium">{tx.amountSent?.toLocaleString('fr-FR') || 0}</td>
                    <td className="p-2 text-right tabular-nums text-amber-700">{tx.waveFee?.toLocaleString('fr-FR') || 0}</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-rose-700">{tx.amountDebited?.toLocaleString('fr-FR') || 0}</td>
                    <td className="p-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold ${meta.color}`} data-testid={`wave-tx-status-${tx.waveRef}`}>{meta.label}</span>
                      {tx.category && (
                        <div className="mt-0.5">
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 text-[9.5px] font-semibold"
                                data-testid={`wave-tx-category-${tx.waveRef}`}>
                            <Tag className="h-2.5 w-2.5" /> {tx.category}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {tx.matchScore ? <span className={`px-1.5 py-0.5 rounded ${tx.matchScore >= 85 ? 'bg-emerald-100 text-emerald-700' : tx.matchScore >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>{tx.matchScore.toFixed(0)}</span> : '—'}
                    </td>
                    <td className="p-2 max-w-[160px] truncate" title={(tx.matchedOps || []).map(o => o.operationNumber).join(', ') || (tx.matchedOperationIds || []).join(', ')}>
                      {(tx.matchedOperationIds || []).length === 0 ? <span className="text-zinc-400">—</span> :
                        <a
                          href={`/admin/operations/${tx.matchedOperationIds[0]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-0.5 text-emerald-700 hover:text-emerald-900 hover:underline font-mono text-[10px]"
                          data-testid={`wave-tx-op-link-${tx.waveRef}`}
                          title="Ouvrir la fiche opération dans un nouvel onglet"
                        >
                          {(tx.matchedOps?.[0]?.operationNumber) || tx.matchedOperationIds[0]}
                          {tx.matchedOperationIds.length > 1 ? ` +${tx.matchedOperationIds.length - 1}` : ''}
                          <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                        </a>
                      }
                    </td>
                    <td className="p-2">
                      {tx.waveFee > 0 ? (() => {
                        const fo = effectiveFeeOwner(tx);
                        const foMeta = {
                          baobab:    { label: 'Baobab',      cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
                          client:    { label: 'Client',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                          chauffeur: { label: 'Chauffeur',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
                          unknown:   { label: 'À vérifier',  cls: 'bg-amber-50 text-amber-800 border-amber-300 font-semibold' },
                        }[fo] || { label: fo, cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' };
                        return (
                          <button onClick={() => switchTab('fees')}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] hover:opacity-75 transition-opacity ${foMeta.cls}`}
                                  title="Classification gérée dans l'onglet Frais Wave — cliquer pour y accéder"
                                  data-testid={`wave-fee-owner-${tx.waveRef}`}>
                            {foMeta.label}
                          </button>
                        );
                      })() : <span className="text-zinc-400 text-[10px]">—</span>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {canMatch && (
                        <button onClick={() => setMatchDialog({ open: true, tx })} className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold"
                                data-testid={`wave-tx-match-${tx.waveRef}`}>
                          {(tx.matchedOperationIds?.length || 0) > 0 ? 'Modifier' : 'Rapprocher'}
                        </button>
                      )}
                      {canUnmatch && (
                        <button onClick={() => handleUnmatch(tx)} className="ml-2 text-rose-600 hover:text-rose-800 text-xs"
                                data-testid={`wave-tx-unmatch-${tx.waveRef}`}>Retirer</button>
                      )}
                      {(tx.matchStatus === 'non_rapprochee' || tx.matchStatus === 'a_verifier' || tx.matchStatus === 'ecart_detecte') && (
                        <button onClick={() => openDiagnose(tx)} className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
                                title="Diagnostiquer pourquoi cette transaction n'est pas rapprochée"
                                data-testid={`wave-tx-diagnose-${tx.waveRef}`}>Diagnostic</button>
                      )}
                      {!isLocked && ['non_rapprochee', 'a_verifier', 'ecart_detecte'].includes(tx.matchStatus) && (
                        <button onClick={() => setCatDialog({ open: true, txIds: [tx.id], txLabel: tx.waveRef })}
                                className="ml-2 text-teal-600 hover:text-teal-800 text-xs"
                                title="Classer comme mouvement hors décaissement (Retrait Express, Dépôt…)"
                                data-testid={`wave-tx-categorize-${tx.waveRef}`}>Catégoriser</button>
                      )}
                      {!isLocked && tx.matchStatus === 'non_concernee_wave' && tx.category && (
                        <button onClick={() => handleUncategorize(tx)}
                                className="ml-2 text-zinc-500 hover:text-zinc-800 text-xs inline-flex items-center gap-0.5"
                                title="Rétablir dans le flux de rapprochement"
                                data-testid={`wave-tx-uncategorize-${tx.waveRef}`}>
                          <RotateCcw className="h-2.5 w-2.5" /> Rétablir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* PAGINATION */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-zinc-200 bg-zinc-50" data-testid="wave-pagination">
          <div className="flex items-center gap-2 text-xs text-zinc-600">
            <span>Lignes par page :</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="text-xs border border-zinc-200 rounded-md px-1.5 py-1 bg-white" data-testid="wave-page-size">
              {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-zinc-400">
              {totalTx === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, totalTx)}`} sur {totalTx}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={page === 0}
                    onClick={() => setPage(page - 1)} data-testid="wave-page-prev">
              <ChevronLeft className="h-3.5 w-3.5" /> Précédent
            </Button>
            <span className="text-xs text-zinc-600 px-2 tabular-nums">
              Page {page + 1} / {Math.max(1, Math.ceil(totalTx / pageSize))}
            </span>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                    disabled={(page + 1) * pageSize >= totalTx}
                    onClick={() => setPage(page + 1)} data-testid="wave-page-next">
              Suivant <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
        </div>{/* /workspace-main */}

        {/* PANNEAU INSIGHTS DU MOIS */}
        <aside className="space-y-3" data-testid="wave-insights-panel">
          <div className="bg-white border border-zinc-200 rounded-xl p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">
              Insights du mois
            </div>
            <div className="space-y-1.5 text-[12.5px]">
              <button onClick={() => { setStatusFilter(''); setActiveTab('transactions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-zinc-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-total">
                <span className="text-zinc-600">Transactions totales</span>
                <span className="font-bold tabular-nums text-zinc-900">{kpis?.transactionsCount || 0}</span>
              </button>
              <button onClick={() => { setStatusFilter('non_rapprochee'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-unmatched">
                <span className="text-zinc-600">Non affectées à revoir</span>
                <span className={`font-bold tabular-nums ${(kpis?.unmatched || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>
                  {kpis?.unmatched || 0}
                </span>
              </button>
              <button onClick={() => { setStatusFilter('a_verifier'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-to-verify">
                <span className="text-zinc-600">À vérifier</span>
                <span className={`font-bold tabular-nums ${(kpis?.toVerify || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>
                  {kpis?.toVerify || 0}
                </span>
              </button>
              <button onClick={() => { setStatusFilter('ecart_detecte'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-rose-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-ecart">
                <span className="text-zinc-600">Écarts à vérifier</span>
                <span className={`font-bold tabular-nums ${(kpis?.ecart || 0) > 0 ? 'text-rose-700' : 'text-zinc-900'}`}>
                  {kpis?.ecart || 0}
                </span>
              </button>
              <button onClick={() => { setStatusFilter('annulee_reversee'); setActiveTab('transactions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-zinc-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-cancelled">
                <span className="text-zinc-600">Annulées / Reversées</span>
                <span className="font-bold tabular-nums text-zinc-900">{kpis?.cancelled || 0}</span>
              </button>
              <button onClick={() => { setActiveTab('fees'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-insight-fees">
                <span className="text-zinc-600">Frais non classés</span>
                <span className={`font-bold tabular-nums ${(kpis?.unvalidatedFees || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>
                  {fxof(kpis?.unvalidatedFees || 0).replace(' XOF', '')}
                </span>
              </button>
            </div>
          </div>

          {/* CHIFFRES DU MOIS (KPIs secondaires déplacés ici) */}
          <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid="wave-secondary-kpis">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">
              Chiffres du mois
            </div>
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex items-center justify-between py-1" data-testid="wave-kpi-sent">
                <span className="text-zinc-600 flex items-center gap-1.5"><Send className="h-3 w-3 text-emerald-600" /> Envoyé chauffeurs</span>
                <span className="font-bold tabular-nums text-zinc-900">{fxof(kpis?.totalSent).replace(' XOF', '')}</span>
              </div>
              <div className="flex items-center justify-between py-1" data-testid="wave-kpi-fees">
                <span className="text-zinc-600 flex items-center gap-1.5"><Coins className="h-3 w-3 text-amber-600" /> Frais Wave</span>
                <span className="font-bold tabular-nums text-zinc-900">{fxof(kpis?.totalFees).replace(' XOF', '')}</span>
              </div>
              <div className="flex items-center justify-between py-1" data-testid="wave-kpi-real-margin">
                <span className="text-zinc-600 flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-blue-600" /> Marge réelle</span>
                <span className={`font-bold tabular-nums ${matchedTotal === 0 ? 'text-zinc-400' : (kpis?.realMargin || 0) < 0 ? 'text-rose-600' : 'text-zinc-900'}`}
                      title={matchedTotal === 0 ? 'Calculée après rapprochement des transactions' : undefined}>
                  {matchedTotal === 0 ? '—' : fxof(kpis?.realMargin).replace(' XOF', '')}
                </span>
              </div>
            </div>
          </div>

          {/* RÉPARTITION PAR CATÉGORIE (tx hors flux) */}
          {kpis?.categoryBreakdown && Object.keys(kpis.categoryBreakdown).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid="wave-category-breakdown">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5 flex items-center gap-1.5">
                <Tag className="h-3 w-3 text-teal-600" /> Hors flux par catégorie
              </div>
              <div className="space-y-1.5 text-[12px]">
                {Object.entries(kpis.categoryBreakdown)
                  .sort((a, b) => (b[1].amount || 0) - (a[1].amount || 0))
                  .map(([cat, v]) => (
                    <button key={cat}
                            onClick={() => { setStatusFilter('non_concernee_wave'); setCategoryFilter(cat); setActiveTab('transactions'); }}
                            className="flex items-center justify-between w-full py-1 hover:bg-teal-50 rounded px-1 -mx-1"
                            data-testid={`wave-cat-breakdown-${cat}`}>
                      <span className="text-zinc-600 truncate flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0" />
                        {cat} <span className="text-zinc-400">({v.count})</span>
                      </span>
                      <span className="font-bold tabular-nums text-zinc-900 shrink-0">{fxof(v.amount).replace(' XOF', '')}</span>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {!isLocked && !isAllPeriods && (
            <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid="wave-preclose-checklist">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">
                Avant de clôturer
              </div>
              <div className="space-y-2 text-[12px]">
                {[
                  { label: 'Vérifier les non affectées', done: (kpis?.unmatched || 0) === 0, count: kpis?.unmatched || 0, action: () => { setStatusFilter('non_rapprochee'); setActiveTab('exceptions'); } },
                  { label: 'Contrôler les écarts', done: (kpis?.ecart || 0) === 0, count: kpis?.ecart || 0, action: () => { setStatusFilter('ecart_detecte'); setActiveTab('exceptions'); } },
                  { label: 'Classer les frais Wave', done: (kpis?.unvalidatedFees || 0) === 0, count: null, action: () => setActiveTab('fees') },
                  { label: 'Consulter le journal d\'audit', done: false, count: null, action: loadAudit, neutral: true },
                ].map((item, i) => (
                  <button key={i} onClick={item.action}
                          className="w-full flex items-start gap-2 py-1 hover:bg-zinc-50 rounded px-1 -mx-1 text-left"
                          data-testid={`wave-checklist-item-${i}`}>
                    {item.neutral ? (
                      <div className="h-4 w-4 rounded-full border-2 border-zinc-300 shrink-0 mt-0.5" />
                    ) : item.done ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-amber-400 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${item.done && !item.neutral ? 'text-emerald-800 line-through' : 'text-zinc-800'}`}>
                        {item.label}
                      </div>
                      {item.count != null && item.count > 0 && (
                        <div className="text-[10.5px] text-amber-700">{item.count} en attente</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {preCloseIssues.length === 0 && (
                <Button size="sm" onClick={() => setCloseDialog(true)}
                        className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                        data-testid="wave-checklist-close">
                  <Lock className="h-3 w-3 mr-1.5" /> Voir le rapport de clôture
                </Button>
              )}
            </div>
          )}
        </aside>
      </div>{/* /workspace-grid */}
      </>
      )}

      {/* OVERVIEW TAB — Insights + Chiffres + Checklist en grille horizontale */}
      {activeTab === 'overview' && kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wave-overview-content">
          {/* Justification du solde — preuve de cadrage comptable */}
          {kpis.openingBalance != null && kpis.lastBalance != null && (
            <div className={`rounded-xl border-2 p-4 md:col-span-2 lg:col-span-3 ${
              (kpis.balanceDelta || 0) === 0 ? 'border-emerald-200 bg-white' : 'border-rose-200 bg-rose-50/40'
            }`} data-testid="wave-balance-justification">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                  <Wallet className="h-3.5 w-3.5 text-emerald-600" /> Justification du solde — {formatPeriodLabel(period)}
                </div>
                {(kpis.balanceDelta || 0) === 0 ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-[11px] font-bold"
                        data-testid="wave-balance-check-ok">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Cadré avec le relevé
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 border border-rose-300 text-rose-800 text-[11px] font-bold"
                        title="Le solde théorique ne correspond pas au solde du relevé : lignes manquantes ou relevé incomplet"
                        data-testid="wave-balance-check-gap">
                    <AlertTriangle className="h-3.5 w-3.5" /> Écart de {fxof(Math.abs(kpis.balanceDelta))} — relevé possiblement incomplet
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-stretch gap-2 text-[12px]">
                <div className="flex-1 min-w-[140px] p-2.5 rounded-lg bg-zinc-50 border border-zinc-200" data-testid="wave-balance-opening">
                  <div className="text-[10px] uppercase font-semibold text-zinc-500">Solde d&apos;ouverture</div>
                  <div className="text-[15px] font-bold tabular-nums text-zinc-900 mt-0.5">{fxof(kpis.openingBalance)}</div>
                </div>
                <div className="flex items-center text-zinc-400 font-bold text-lg px-0.5">+</div>
                <div className="flex-1 min-w-[140px] p-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="wave-balance-inflows">
                  <div className="text-[10px] uppercase font-semibold text-emerald-700">Entrées</div>
                  <div className="text-[15px] font-bold tabular-nums text-emerald-800 mt-0.5">{fxof(kpis.totalInflows || 0)}</div>
                  <div className="text-[10px] text-emerald-700/70">{kpis.inflowsCount || 0} transaction(s)</div>
                </div>
                <div className="flex items-center text-zinc-400 font-bold text-lg px-0.5">−</div>
                <div className="flex-1 min-w-[140px] p-2.5 rounded-lg bg-rose-50 border border-rose-200" data-testid="wave-balance-outflows">
                  <div className="text-[10px] uppercase font-semibold text-rose-700">Sorties</div>
                  <div className="text-[15px] font-bold tabular-nums text-rose-800 mt-0.5">
                    {fxof(kpis.feesInBalance ? kpis.totalDebited : kpis.totalSent)}
                  </div>
                  <div className="text-[10px] text-rose-700/70">
                    {kpis.feesInBalance ? 'envoyé + frais Wave' : `hors frais Wave (${fxof(kpis.totalFees)} suivis à part)`}
                  </div>
                </div>
                <div className="flex items-center text-zinc-400 font-bold text-lg px-0.5">=</div>
                <div className="flex-1 min-w-[140px] p-2.5 rounded-lg bg-blue-50 border border-blue-200" data-testid="wave-balance-expected">
                  <div className="text-[10px] uppercase font-semibold text-blue-700">Solde théorique</div>
                  <div className="text-[15px] font-bold tabular-nums text-blue-800 mt-0.5">{fxof(kpis.expectedClosing)}</div>
                </div>
                <div className="flex items-center text-zinc-400 font-bold text-sm px-0.5">vs</div>
                <div className={`flex-1 min-w-[140px] p-2.5 rounded-lg border-2 ${
                  (kpis.balanceDelta || 0) === 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300'
                }`} data-testid="wave-balance-actual">
                  <div className={`text-[10px] uppercase font-semibold ${(kpis.balanceDelta || 0) === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Solde relevé</div>
                  <div className={`text-[15px] font-bold tabular-nums mt-0.5 ${(kpis.balanceDelta || 0) === 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {fxof(kpis.lastBalance)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Insights du mois */}
          <div className="bg-white border border-zinc-200 rounded-xl p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">Insights du mois</div>
            <div className="space-y-1.5 text-[12.5px]">
              <button onClick={() => { setStatusFilter(''); setActiveTab('transactions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-zinc-50 rounded px-1 -mx-1"
                      data-testid="wave-overview-total">
                <span className="text-zinc-600">Transactions totales</span>
                <span className="font-bold tabular-nums text-zinc-900">{kpis.transactionsCount || 0}</span>
              </button>
              <button onClick={() => { setStatusFilter('non_rapprochee'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-overview-unmatched">
                <span className="text-zinc-600">Non affectées à revoir</span>
                <span className={`font-bold tabular-nums ${(kpis.unmatched || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>{kpis.unmatched || 0}</span>
              </button>
              <button onClick={() => { setStatusFilter('a_verifier'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-overview-to-verify">
                <span className="text-zinc-600">À vérifier</span>
                <span className={`font-bold tabular-nums ${(kpis.toVerify || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>{kpis.toVerify || 0}</span>
              </button>
              <button onClick={() => { setStatusFilter('ecart_detecte'); setActiveTab('exceptions'); }}
                      className="flex items-center justify-between w-full py-1 hover:bg-rose-50 rounded px-1 -mx-1"
                      data-testid="wave-overview-ecart">
                <span className="text-zinc-600">Écarts à vérifier</span>
                <span className={`font-bold tabular-nums ${(kpis.ecart || 0) > 0 ? 'text-rose-700' : 'text-zinc-900'}`}>{kpis.ecart || 0}</span>
              </button>
              <button onClick={() => setActiveTab('fees')}
                      className="flex items-center justify-between w-full py-1 hover:bg-amber-50 rounded px-1 -mx-1"
                      data-testid="wave-overview-fees">
                <span className="text-zinc-600">Frais non classés</span>
                <span className={`font-bold tabular-nums ${(kpis.unvalidatedFees || 0) > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>
                  {fxof(kpis.unvalidatedFees || 0).replace(' XOF', '')}
                </span>
              </button>
            </div>
          </div>

          {/* Chiffres du mois */}
          <div className="bg-white border border-zinc-200 rounded-xl p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">Chiffres du mois</div>
            <div className="space-y-1.5 text-[12.5px]">
              <div className="flex items-center justify-between py-1">
                <span className="text-zinc-600 flex items-center gap-1.5"><Send className="h-3 w-3 text-emerald-600" /> Envoyé chauffeurs</span>
                <span className="font-bold tabular-nums text-zinc-900">{fxof(kpis.totalSent).replace(' XOF', '')}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-zinc-600 flex items-center gap-1.5"><Coins className="h-3 w-3 text-amber-600" /> Frais Wave</span>
                <span className="font-bold tabular-nums text-zinc-900">{fxof(kpis.totalFees).replace(' XOF', '')}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-zinc-600 flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-blue-600" /> Marge réelle</span>
                <span className={`font-bold tabular-nums ${matchedTotal === 0 ? 'text-zinc-400' : (kpis.realMargin || 0) < 0 ? 'text-rose-600' : 'text-zinc-900'}`}
                      title={matchedTotal === 0 ? 'Calculée après rapprochement des transactions' : undefined}>
                  {matchedTotal === 0 ? '—' : fxof(kpis.realMargin).replace(' XOF', '')}
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-zinc-600">Solde Wave fin de période</span>
                <span className="font-bold tabular-nums text-zinc-900">{fxof(kpis.lastBalance).replace(' XOF', '')}</span>
              </div>
            </div>
          </div>

          {/* Hors flux par catégorie */}
          {kpis.categoryBreakdown && Object.keys(kpis.categoryBreakdown).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-3.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5 flex items-center gap-1.5">
                <Tag className="h-3 w-3 text-teal-600" /> Hors flux par catégorie
              </div>
              <div className="space-y-1.5 text-[12px]">
                {Object.entries(kpis.categoryBreakdown)
                  .sort((a, b) => (b[1].amount || 0) - (a[1].amount || 0))
                  .map(([cat, v]) => (
                    <button key={cat}
                            onClick={() => { setStatusFilter('non_concernee_wave'); setCategoryFilter(cat); setActiveTab('transactions'); }}
                            className="flex items-center justify-between w-full py-1 hover:bg-teal-50 rounded px-1 -mx-1"
                            data-testid={`wave-overview-cat-${cat}`}>
                      <span className="text-zinc-600 truncate flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400 shrink-0" />
                        {cat} <span className="text-zinc-400">({v.count})</span>
                      </span>
                      <span className="font-bold tabular-nums text-zinc-900 shrink-0">{fxof(v.amount).replace(' XOF', '')}</span>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Checklist pré-clôture — colonne pleine largeur si utile */}
          {!isLocked && !isAllPeriods && (
            <div className="bg-white border border-zinc-200 rounded-xl p-3.5 md:col-span-2 lg:col-span-3" data-testid="wave-overview-checklist">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">Avant de clôturer</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-[12px]">
                {[
                  { label: 'Vérifier les non affectées', done: (kpis.unmatched || 0) === 0, count: kpis.unmatched || 0, action: () => { setStatusFilter('non_rapprochee'); setActiveTab('exceptions'); } },
                  { label: 'Contrôler les écarts', done: (kpis.ecart || 0) === 0, count: kpis.ecart || 0, action: () => { setStatusFilter('ecart_detecte'); setActiveTab('exceptions'); } },
                  { label: 'Classer les frais Wave', done: (kpis.unvalidatedFees || 0) === 0, count: null, action: () => setActiveTab('fees') },
                  { label: 'Journal d\'audit', done: false, count: null, action: loadAudit, neutral: true },
                ].map((item, i) => (
                  <button key={i} onClick={item.action}
                          className="flex items-start gap-2 py-1.5 px-2 rounded-lg border border-zinc-100 hover:border-emerald-300 hover:bg-emerald-50 text-left transition-colors"
                          data-testid={`wave-overview-checklist-${i}`}>
                    {item.neutral ? <div className="h-4 w-4 rounded-full border-2 border-zinc-300 shrink-0 mt-0.5" />
                     : item.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                     : <div className="h-4 w-4 rounded-full border-2 border-amber-400 shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${item.done && !item.neutral ? 'text-emerald-800' : 'text-zinc-800'}`}>{item.label}</div>
                      {item.count != null && item.count > 0 && (
                        <div className="text-[10.5px] text-amber-700">{item.count} en attente</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* INFLOWS TAB — Encaissements (workflow manuel strict) */}
      {activeTab === 'inflows' && (
        <div data-testid="wave-inflows-content">
          <WaveInflowsPanel period={period} />
        </div>
      )}

      {/* FEES TAB — Vue dédiée aux Frais Wave (tableau spécifique + KPIs + attribution propriétaire) */}
      {activeTab === 'fees' && (
        <div className="space-y-4" data-testid="wave-fees-content">
          {/* Bannière pédagogique */}
          <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-2.5" data-testid="wave-fees-banner">
            <Coins className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[12.5px] text-amber-900">
              <b>Gestion des Frais Wave</b> — Chaque transaction Wave génère des frais. Assignez leur propriétaire
              (<b>Baobab</b> = frais interne · <b>Client</b> = à refacturer · <b>Chauffeur</b> = à déduire) pour un
              traitement comptable propre. Une même transaction peut être rapprochée ou non — cela n&apos;empêche pas de classer ses frais.
            </div>
          </div>

          {/* KPIs Frais */}
          {(() => {
            const feeTxs = transactions.filter(t => (t.waveFee || 0) > 0);
            const byOwner = feeTxs.reduce((acc, t) => {
              const k = effectiveFeeOwner(t);
              acc[k] = (acc[k] || 0) + (t.waveFee || 0);
              return acc;
            }, {});
            const totalFees = feeTxs.reduce((s, t) => s + (t.waveFee || 0), 0);
            const unclassifiedCount = feeTxs.filter(t => effectiveFeeOwner(t) === 'unknown').length;
            const ownerMeta = {
              baobab:    { label: 'Baobab',    color: 'text-zinc-800',    bg: 'bg-zinc-50',    border: 'border-zinc-200' },
              client:    { label: 'Client',    color: 'text-emerald-800', bg: 'bg-emerald-50', border: 'border-emerald-200' },
              chauffeur: { label: 'Chauffeur', color: 'text-blue-800',    bg: 'bg-blue-50',    border: 'border-blue-200' },
              unknown:   { label: 'À vérifier', color: 'text-amber-800',  bg: 'bg-amber-50',   border: 'border-amber-200' },
            };
            return (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 rounded-xl border border-zinc-200 bg-white" data-testid="wave-fees-kpi-total">
                  <div className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Total frais</div>
                  <div className="text-lg font-bold tabular-nums text-zinc-900 mt-1">{fxof(totalFees)}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{feeTxs.length} transaction(s)</div>
                </div>
                <div className="p-3 rounded-xl border border-amber-200 bg-amber-50" data-testid="wave-fees-kpi-unclassified">
                  <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Non classés</div>
                  <div className="text-lg font-bold tabular-nums text-amber-900 mt-1">{unclassifiedCount}</div>
                  <div className="text-[10px] text-amber-700 mt-0.5">{fxof(byOwner.unknown || 0)}</div>
                </div>
                {['baobab', 'client', 'chauffeur'].map((k) => {
                  const m = ownerMeta[k];
                  return (
                    <div key={k} className={`p-3 rounded-xl border ${m.border} ${m.bg}`} data-testid={`wave-fees-kpi-${k}`}>
                      <div className={`text-[10px] uppercase font-bold tracking-wider ${m.color}`}>{m.label}</div>
                      <div className={`text-lg font-bold tabular-nums mt-1 ${m.color}`}>{fxof(byOwner[k] || 0)}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {totalFees > 0 ? Math.round(((byOwner[k] || 0) / totalFees) * 100) : 0}% du total
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Filtre recherche compact */}
          <div className="flex flex-wrap items-center gap-2 p-2 bg-white border border-zinc-200 rounded-lg">
            <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Filter className="h-3 w-3" /> Filtres frais</div>
            <select value={feesOwnerFilter} onChange={(e) => setFeesOwnerFilter(e.target.value)}
                    className="text-xs border border-zinc-200 rounded-md px-2 py-1" data-testid="wave-fees-owner-filter">
              <option value="">Tous propriétaires</option>
              <option value="unknown">À vérifier (non classés)</option>
              <option value="baobab">Baobab</option>
              <option value="client">Client</option>
              <option value="chauffeur">Chauffeur</option>
            </select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Réf Wave · bénéficiaire…"
                     className="pl-7 text-xs h-8" data-testid="wave-fees-query" />
            </div>
            <span className="text-xs text-zinc-500 font-semibold tabular-nums">
              {transactions.filter(t => (t.waveFee || 0) > 0 && (!feesOwnerFilter || effectiveFeeOwner(t) === feesOwnerFilter)).length} frais
            </span>
          </div>

          {/* Actions de masse — attribution rapide du propriétaire des frais */}
          {(() => {
            const visibleFeeIds = transactions
              .filter(t => (t.waveFee || 0) > 0 && (!feesOwnerFilter || effectiveFeeOwner(t) === feesOwnerFilter))
              .map(t => t.id);
            return (
              <BulkActionBar
                count={feesSelection.length}
                total={visibleFeeIds.length}
                label="frais"
                busy={feesBulkBusy}
                onSelectAll={() => setFeesSelection(visibleFeeIds)}
                onClear={() => setFeesSelection([])}
                testid="wave-fees-bulk-bar"
                actions={[
                  { key: 'baobab', label: 'Baobab (interne)', className: 'border-zinc-300 text-zinc-700 hover:bg-zinc-100',
                    onClick: () => bulkAssignFeeOwner('baobab', 'Baobab (frais interne)') },
                  { key: 'client', label: 'Client (refacturer)', className: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50',
                    onClick: () => bulkAssignFeeOwner('client', 'Client (refacturer)') },
                  { key: 'chauffeur', label: 'Chauffeur (déduire)', className: 'border-sky-300 text-sky-700 hover:bg-sky-50',
                    onClick: () => bulkAssignFeeOwner('chauffeur', 'Chauffeur (déduire)') },
                ]}
              />
            );
          })()}

          {/* Table dédiée Frais Wave */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="wave-fees-table">
                <thead className="bg-zinc-50 text-zinc-600 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-2 text-left w-8">
                      {(() => {
                        const visibleFeeIds = transactions
                          .filter(t => (t.waveFee || 0) > 0 && (!feesOwnerFilter || effectiveFeeOwner(t) === feesOwnerFilter))
                          .map(t => t.id);
                        return (
                          <input
                            type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                            checked={feesSelection.length > 0 && feesSelection.length === visibleFeeIds.length}
                            onChange={(e) => setFeesSelection(e.target.checked ? visibleFeeIds : [])}
                            disabled={visibleFeeIds.length === 0}
                            title="Sélectionner tous les frais affichés"
                            data-testid="wave-fees-select-all"
                          />
                        );
                      })()}
                    </th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Réf Wave</th>
                    <th className="p-2 text-left">Bénéficiaire</th>
                    <th className="p-2 text-right">Envoyé</th>
                    <th className="p-2 text-right bg-amber-50 text-amber-900">Frais Wave</th>
                    <th className="p-2 text-center">Rapprochement</th>
                    <th className="p-2 text-left">Opérations liées</th>
                    <th className="p-2 text-left">Propriétaire des frais</th>
                    <th className="p-2 text-right">Refacturable ?</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {(() => {
                    const feeTxs = transactions.filter(t => (t.waveFee || 0) > 0
                      && (!feesOwnerFilter || effectiveFeeOwner(t) === feesOwnerFilter));
                    if (feeTxs.length === 0) {
                      return (
                        <tr><td colSpan={10} className="py-10 px-4 text-center text-zinc-400 text-xs" data-testid="wave-fees-empty">
                          Aucun frais Wave à afficher pour {formatPeriodLabel(period)}
                          {feesOwnerFilter && ` (propriétaire "${feesOwnerFilter}")`}.
                        </td></tr>
                      );
                    }
                    return feeTxs.map((tx) => {
                      const meta = STATUS_META[tx.matchStatus] || { label: tx.matchStatus, color: 'bg-zinc-100 text-zinc-700' };
                      const isReconciled = ['rapprochee_auto', 'rapprochee_manuelle', 'rapprochement_partiel', 'paiement_groupe'].includes(tx.matchStatus);
                      return (
                        <tr key={tx.id}
                            className={`hover:bg-zinc-50 ${feesSelection.includes(tx.id) ? 'bg-emerald-50/60' : ''}`}
                            data-testid={`wave-fees-row-${tx.waveRef}`}>
                          <td className="p-2">
                            <input
                              type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                              checked={feesSelection.includes(tx.id)}
                              onChange={(e) => setFeesSelection(prev => (
                                e.target.checked ? [...prev, tx.id] : prev.filter(id => id !== tx.id)
                              ))}
                              data-testid={`wave-fees-select-${tx.waveRef}`}
                            />
                          </td>
                          <td className="p-2 whitespace-nowrap text-zinc-600">{fmtDate(tx.date)}</td>
                          <td className="p-2 font-mono text-zinc-700 truncate max-w-[130px]" title={tx.waveRef}>{tx.waveRef}</td>
                          <td className="p-2 truncate max-w-[140px]" title={tx.beneficiaryName}>
                            <div className="font-medium">{tx.beneficiaryName || '—'}</div>
                            <div className="text-[10px] text-zinc-500">{tx.beneficiaryPhone || ''}</div>
                          </td>
                          <td className="p-2 text-right tabular-nums text-zinc-700">{tx.amountSent?.toLocaleString('fr-FR') || 0}</td>
                          <td className="p-2 text-right tabular-nums font-bold text-amber-800 bg-amber-50/40">
                            {tx.waveFee?.toLocaleString('fr-FR') || 0}
                          </td>
                          <td className="p-2 text-center">
                            {isReconciled ? (
                              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                                <CheckCircle2 className="h-3 w-3" /> Rapprochée
                              </span>
                            ) : (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold ${meta.color}`}>
                                {meta.label}
                              </span>
                            )}
                          </td>
                          <td className="p-2 max-w-[160px]">
                            {(tx.matchedOperationIds || []).length === 0 ? <span className="text-zinc-400 text-[10px]">—</span> :
                              <a href={`/admin/operations/${tx.matchedOperationIds[0]}`}
                                 target="_blank" rel="noopener noreferrer"
                                 onClick={(e) => e.stopPropagation()}
                                 className="inline-flex items-center gap-0.5 text-emerald-700 hover:text-emerald-900 hover:underline font-mono text-[10px]"
                                 data-testid={`wave-fees-op-link-${tx.waveRef}`}
                                 title="Ouvrir la fiche opération dans un nouvel onglet">
                                {(tx.matchedOps?.[0]?.operationNumber) || tx.matchedOperationIds[0]}
                                {tx.matchedOperationIds.length > 1 ? ` +${tx.matchedOperationIds.length - 1}` : ''}
                                <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                              </a>
                            }
                          </td>
                          <td className="p-2">
                            <select value={effectiveFeeOwner(tx)}
                                    onChange={(e) => validateFees([tx.id], e.target.value)}
                                    disabled={isLocked}
                                    className={`text-[11px] border rounded px-1.5 py-1 w-full ${
                                      effectiveFeeOwner(tx) === 'unknown'
                                        ? 'border-amber-300 bg-amber-50 text-amber-900 font-semibold'
                                        : 'border-zinc-200 bg-white'
                                    }`}
                                    data-testid={`wave-fees-owner-${tx.waveRef}`}>
                              <option value="unknown">— À vérifier —</option>
                              <option value="baobab">Baobab (frais interne)</option>
                              <option value="client">Client (refacturer)</option>
                              <option value="chauffeur">Chauffeur (déduire)</option>
                            </select>
                          </td>
                          <td className="p-2 text-right">
                            {effectiveFeeOwner(tx) === 'client' ? (
                              <span className="text-[10px] font-semibold text-emerald-700 inline-flex items-center gap-0.5">
                                <ArrowUpRight className="h-2.5 w-2.5" /> +{tx.waveFee?.toLocaleString('fr-FR')}
                              </span>
                            ) : effectiveFeeOwner(tx) === 'chauffeur' ? (
                              <span className="text-[10px] font-semibold text-blue-700 inline-flex items-center gap-0.5">
                                <ArrowDownRight className="h-2.5 w-2.5" /> -{tx.waveFee?.toLocaleString('fr-FR')}
                              </span>
                            ) : effectiveFeeOwner(tx) === 'baobab' ? (
                              <span className="text-[10px] text-zinc-500">Perte interne</span>
                            ) : (
                              <span className="text-[10px] text-amber-600 italic">À classer</span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* REPORTS TAB — Liste des rapports générés + CTA */}
      {activeTab === 'reports' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="wave-reports-content">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Rapports pour {formatPeriodLabel(period)}
              </div>
              <div className="text-[12px] text-zinc-500 mt-0.5">
                Générez et téléchargez les rapports mensuels (Excel CFO et PDF) — utiles pour la clôture et l&apos;archivage.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => generateReport('xlsx')} disabled={isAllPeriods || generatingReport}
                      className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="wave-reports-gen-xlsx">
                {generatingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />}
                Générer Excel CFO
              </Button>
              <Button size="sm" variant="outline" onClick={() => generateReport('pdf')} disabled={isAllPeriods || generatingReport}
                      className="h-8 text-xs" data-testid="wave-reports-gen-pdf">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5 text-rose-600" /> Générer PDF
              </Button>
            </div>
          </div>
          {isAllPeriods && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              Sélectionnez un mois spécifique pour générer un rapport.
            </div>
          )}
          {reports.length === 0 ? (
            <div className="text-center py-10 text-zinc-500" data-testid="wave-reports-empty">
              <FileSpreadsheet className="h-10 w-10 mx-auto text-zinc-300 mb-2" />
              <div className="text-sm font-medium text-zinc-700 mb-1">Aucun rapport généré pour {formatPeriodLabel(period)}</div>
              <div className="text-xs">Cliquez sur « Générer Excel CFO » ou « Générer PDF » ci-dessus pour créer votre premier rapport.</div>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {reports.map(r => {
                const isPdf = !!r.pdfPath;
                const fname = (r.pdfPath || r.xlsxPath || '').split('/').pop();
                return (
                  <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`wave-reports-row-${r.id.slice(0,8)}`}>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isPdf ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {isPdf ? 'PDF' : 'XLSX'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs truncate">{fname}</div>
                      <div className="text-[10.5px] text-zinc-400">{fmtDate(r.createdAt)} · {r.status === 'locked' ? 'verrouillé' : 'brouillon'}</div>
                    </div>
                    {r.status === 'locked' && <Lock className="h-3 w-3 text-rose-500" />}
                    <Button size="sm" variant="outline" onClick={() => downloadReport(r.id, fname)}
                            className="h-7 text-xs" data-testid={`wave-reports-download-${r.id.slice(0,8)}`}>
                      <Download className="h-3 w-3 mr-1" /> Télécharger
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AUDIT PANEL */}
      {showAudit && (
        <Dialog open={showAudit} onOpenChange={setShowAudit}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="wave-audit-dialog">
            <DialogHeader>
              <DialogTitle>Journal d&apos;audit Wave — {period} ({auditLogs.length} entrées)</DialogTitle>
            </DialogHeader>
            <div className="divide-y divide-zinc-100 text-xs">
              {auditLogs.length === 0 && <div className="text-center text-zinc-400 py-6">Aucune action tracée sur cette période.</div>}
              {auditLogs.map((l, i) => (
                <div key={i} className="py-2 grid grid-cols-6 gap-2">
                  <div className="text-zinc-500 col-span-2">{fmtDate(l.timestamp)}</div>
                  <div className="col-span-2 truncate">{l.userEmail}</div>
                  <div className="col-span-2 font-mono text-emerald-700">{l.action}</div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ManualMatchDialog open={matchDialog.open} tx={matchDialog.tx}
                         onClose={() => setMatchDialog({ open: false, tx: null })}
                         onSuccess={load} />
      <ReopenDialog open={reopenDialog} onClose={() => setReopenDialog(false)}
                    period={period} onSuccess={load} />
      <WaveAiProposalsDialog open={aiDialog.open} result={aiDialog.result} analyzing={aiDialog.analyzing}
                             onClose={() => setAiDialog({ open: false, result: null, analyzing: false })}
                             onApplied={() => { setSelectedIds(new Set()); load(); }} />
      <WaveCategorizeDialog open={catDialog.open} txIds={catDialog.txIds} txLabel={catDialog.txLabel}
                            onClose={() => setCatDialog({ open: false, txIds: [], txLabel: null })}
                            onSuccess={() => { setSelectedIds(new Set()); load(); }} />

      {/* DIALOG CLÔTURE SÉCURISÉE (checklist) */}
      <Dialog open={closeDialog} onOpenChange={(v) => !v && setCloseDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="wave-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-rose-600" />
              Clôturer {formatPeriodLabel(period)}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              La clôture verrouille toutes les transactions de ce mois. Une réouverture explicite sera nécessaire pour toute modification ultérieure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Checklist de pré-clôture</div>
            <div className="space-y-2 text-[13px]">
              {[
                { label: 'Toutes les transactions sont affectées ou justifiées', ok: (kpis?.unmatched || 0) === 0, hint: kpis?.unmatched ? `${kpis.unmatched} non affectée(s)` : null },
                { label: 'Tous les frais Wave sont classés', ok: (kpis?.unvalidatedFees || 0) === 0, hint: kpis?.unvalidatedFees ? `${fxof(kpis.unvalidatedFees)} non classés` : null },
                { label: 'Tous les écarts sont justifiés', ok: (kpis?.ecart || 0) === 0, hint: kpis?.ecart ? `${kpis.ecart} écart(s)` : null },
                { label: 'Les transactions annulées sont exclues', ok: true },
                { label: 'Le rapport Excel a été généré', ok: reports.some(r => r.xlsxPath), hint: !reports.some(r => r.xlsxPath) ? 'Générez d\'abord un Excel CFO' : null },
                { label: 'Le journal d\'audit est disponible', ok: true },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`wave-close-check-${i}`}>
                  {item.ok
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    : <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className={item.ok ? 'text-zinc-800' : 'text-amber-900 font-medium'}>{item.label}</div>
                    {item.hint && <div className="text-[11px] text-amber-700 mt-0.5">{item.hint}</div>}
                  </div>
                </div>
              ))}
            </div>
            {preCloseBlockingIssues.length > 0 ? (
              <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-3" data-testid="wave-close-blocked">
                <div className="text-sm font-bold text-rose-900 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> Clôture impossible
                </div>
                <div className="text-[12px] text-rose-800">
                  {preCloseBlockingIssues.map(i => i.label).join(' · ')} — traitez ces points avant de clôturer.
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3" data-testid="wave-close-ready">
                <div className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Clôture possible
                </div>
                <div className="text-[12px] text-emerald-800">Le mois peut être verrouillé.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)} data-testid="wave-close-cancel">Annuler</Button>
            <Button onClick={closeMonth} disabled={preCloseBlockingIssues.length > 0}
                    className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="wave-close-confirm">
              <Lock className="h-3.5 w-3.5 mr-1.5" /> Clôturer et verrouiller
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG DIAGNOSTIC RAPPROCHEMENT */}
      <Dialog open={diagDialog.open} onOpenChange={(v) => !v && setDiagDialog({ open: false, tx: null, data: null, loading: false })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="wave-diagnose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-600" />
              Diagnostic de rapprochement
              {diagDialog.tx?.waveRef && (
                <span className="text-xs font-mono text-zinc-500">· {diagDialog.tx.waveRef}</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Analyse détaillée : références détectées, chauffeur base, candidats et décision.
            </DialogDescription>
          </DialogHeader>
          {diagDialog.loading && (
            <div className="flex items-center justify-center py-10 text-zinc-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Analyse en cours…
            </div>
          )}
          {diagDialog.data && !diagDialog.loading && (
            <div className="space-y-4 text-sm">
              {/* Résumé transaction */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-zinc-500">Bénéficiaire :</span> <span className="font-medium">{diagDialog.data.beneficiaryName || '—'}</span></div>
                <div><span className="text-zinc-500">Téléphone :</span> <span className="font-mono">{diagDialog.data.beneficiaryPhone || '—'}</span></div>
                <div><span className="text-zinc-500">Montant :</span> <span className="font-semibold tabular-nums">{fxof(diagDialog.data.amountSent)}</span></div>
                <div><span className="text-zinc-500">Statut :</span> <Badge variant="outline" className="text-[10px]">{STATUS_META[diagDialog.data.matchStatus]?.label || diagDialog.data.matchStatus}</Badge></div>
                <div className="col-span-2"><span className="text-zinc-500">Commentaire Wave :</span> <span className="font-mono text-[11px]">{diagDialog.data.comment || '—'}</span></div>
                <div className="col-span-2"><span className="text-zinc-500">Réf. client :</span> <span className="font-mono text-[11px]">{diagDialog.data.customerRef || '—'}</span></div>
              </div>

              {/* Références extraites */}
              <div>
                <div className="text-xs font-semibold text-zinc-700 mb-1.5">Références détectées dans le texte</div>
                <div className="flex flex-wrap gap-1.5">
                  {(diagDialog.data.extractedRefs || []).length === 0 && (diagDialog.data.extractedPrefixes || []).length === 0 && (
                    <span className="text-xs text-zinc-400 italic">Aucune référence détectée</span>
                  )}
                  {(diagDialog.data.extractedRefs || []).map((r, i) => (
                    <Badge key={`r${i}`} className="bg-emerald-100 text-emerald-800 border-emerald-200 font-mono text-[10px]">Réf exacte : {r}</Badge>
                  ))}
                  {(diagDialog.data.extractedPrefixes || []).map((p, i) => (
                    <Badge key={`p${i}`} className="bg-blue-100 text-blue-800 border-blue-200 font-mono text-[10px]">Préfixe : {p}</Badge>
                  ))}
                </div>
              </div>

              {/* Chauffeur base */}
              <div>
                <div className="text-xs font-semibold text-zinc-700 mb-1.5 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" /> Chauffeur base identifié
                </div>
                {diagDialog.data.driverBase ? (
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs">
                    <div><span className="font-medium text-emerald-900">{diagDialog.data.driverBase.name || '—'}</span> · <span className="font-mono">{diagDialog.data.driverBase.phone || '—'}</span></div>
                    <div className="text-emerald-700 text-[10px] mt-0.5">Matché via {diagDialog.data.driverBase.matchedBy === 'phone' ? '📞 téléphone' : '📝 nom'}</div>
                  </div>
                ) : (
                  <div className="text-xs text-zinc-400 italic">Aucun chauffeur base identifié (ni par téléphone, ni par nom).</div>
                )}
              </div>

              {/* Candidats */}
              <div>
                <div className="text-xs font-semibold text-zinc-700 mb-1.5 flex items-center justify-between">
                  <span>Opérations candidates ({diagDialog.data.candidatesCount})</span>
                  {diagDialog.data.candidates?.[0] && (
                    <span className="text-[10px] text-zinc-500">Meilleur score : {diagDialog.data.candidates[0].score.toFixed(0)}</span>
                  )}
                </div>
                {(diagDialog.data.candidates || []).length === 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Aucune opération candidate trouvée.</div>
                        <div className="mt-1 text-amber-700">
                          Causes possibles : opération non créée en base, référence erronée dans le commentaire Wave, ou chauffeur non trouvé.
                          Vérifiez que l&apos;opération référencée (préfixe ci-dessus) existe bien dans le module Opérations.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {(diagDialog.data.candidates || []).slice(0, 10).map((c, i) => (
                      <div key={i} className="rounded border border-zinc-200 p-2 text-[11px] bg-white">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono font-semibold text-zinc-800 truncate">{c.operationNumber || '—'}</div>
                          <span className={`px-1.5 py-0.5 rounded font-bold ${c.score >= 85 ? 'bg-emerald-100 text-emerald-700' : c.score >= 45 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
                            Score {c.score.toFixed(0)}
                          </span>
                        </div>
                        <div className="text-zinc-500 mt-1 grid grid-cols-3 gap-1 text-[10px]">
                          <div>Tarif : <span className="tabular-nums">{c.carrierPrice ? fxof(c.carrierPrice) : '—'}</span></div>
                          <div>Chauffeur : {c.driverName || '—'}</div>
                          <div>Tel : <span className="font-mono">{c.driverPhone || '—'}</span></div>
                        </div>
                        <div className="text-zinc-500 mt-1 flex flex-wrap gap-1 text-[10px]">
                          <span className="px-1 py-0.5 rounded bg-zinc-100">
                            Réf : {c.breakdown?.ref?.score || 0} {c.breakdown?.ref?.kind ? `(${c.breakdown.ref.kind})` : ''}
                          </span>
                          <span className="px-1 py-0.5 rounded bg-zinc-100">
                            Montant : {c.breakdown?.amount?.score || 0}
                            {c.breakdown?.amount?.kind ? ` (${c.breakdown.amount.kind})` : ''}
                          </span>
                          <span className="px-1 py-0.5 rounded bg-zinc-100">
                            Chauffeur : {c.breakdown?.driver?.score?.toFixed(1) || 0}
                            {c.breakdown?.driver?.phoneMatched ? ' 📞✓' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Décision */}
              <div className="rounded-lg border border-zinc-200 p-3 bg-zinc-50">
                <div className="text-xs font-semibold text-zinc-700 mb-1">Décision automatique</div>
                <div className="text-xs">
                  {diagDialog.data.decision.autoApplied && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ Rapprochement auto appliqué</Badge>}
                  {diagDialog.data.decision.toVerify && <Badge className="bg-amber-100 text-amber-800 border-amber-200">⚠ À vérifier manuellement</Badge>}
                  {!diagDialog.data.decision.autoApplied && !diagDialog.data.decision.toVerify && (
                    <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200">✗ Aucun match automatique possible</Badge>
                  )}
                  <span className="ml-2 text-zinc-500">
                    (meilleur score : {diagDialog.data.decision.bestScore.toFixed(1)} — seuils : ≥45 à vérifier, ≥85 auto)
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiagDialog({ open: false, tx: null, data: null, loading: false })}>Fermer</Button>
            {diagDialog.data && diagDialog.data.candidatesCount > 0 && diagDialog.tx && (
              <Button onClick={() => {
                setDiagDialog({ open: false, tx: null, data: null, loading: false });
                setMatchDialog({ open: true, tx: diagDialog.tx });
              }} data-testid="wave-diagnose-to-manual-match">
                <Link2 className="h-4 w-4 mr-2" /> Rapprocher manuellement
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG SAUVEGARDE RECHERCHE */}
      <Dialog open={saveSearchOpen} onOpenChange={(v) => !v && setSaveSearchOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="wave-save-search-dialog">
          <DialogHeader><DialogTitle>Sauvegarder la recherche</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-zinc-500">
              Filtres courants : {statusFilter ? STATUS_META[statusFilter]?.label : 'Tous statuts'}
              {query ? ` · « ${query} »` : ''} · {outflowOnly ? 'Sorties' : 'Toutes'} · {period}
            </div>
            <Label className="text-xs font-semibold">Nom de la recherche</Label>
            <Input value={saveSearchName} onChange={(e) => setSaveSearchName(e.target.value)}
                   placeholder="ex : Écarts non traités" data-testid="wave-save-search-name"
                   onKeyDown={(e) => e.key === 'Enter' && saveCurrentSearch()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveSearchOpen(false)}>Annuler</Button>
            <Button onClick={saveCurrentSearch} data-testid="wave-save-search-submit">
              <BookmarkPlus className="h-4 w-4 mr-2" /> Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaveReconciliationTab;
